/**
 * Zoketo OTP Login System
 * This script handles OTP-based authentication, Google SSO, and Facebook SSO integration
 * for Shopify stores. It provides a modern, secure login experience with multiple
 * authentication methods.
 */

console.log("🚀 login-override.js - Script starting to load...");

/**
 * Check if the current store should hide the "Powered by Zoketo" text
 * Fetches shop domain and hide list from window.hyperOTPUserData (set by login-embed.liquid)
 * @returns {boolean} True if the powered by text should be hidden
 */
function shouldHidePoweredBy() {
  try {
    // Get shop domain from settings (primary source) or hyperOTPSettings (fallback)
    const shop = window.hyperOTPUserData?.settings?.shop || window.hyperOTPSettings?.shop;
    if (!shop) return false;

    // Get the list of stores that should hide powered by text (from login-embed.liquid)
    const hidePoweredByStores = window.hyperOTPUserData?.hidePoweredByStores || [];

    return hidePoweredByStores.includes(shop);
  } catch (error) {
    console.error('Error checking powered by visibility:', error);
    return false;
  }
}

// Load Inter font from Google Fonts
(function injectInterFont() {
  console.log("📝 Injecting Inter font...");
  if (document.getElementById('hyperotp-inter-font-link')) return;
  const link = document.createElement('link');
  link.id = 'hyperotp-inter-font-link';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;450;500;550;600;650;700&display=swap';
  document.head.appendChild(link);
  console.log("✅ Inter font injected");
})();

// Add a temporary CSS at the very top to immediately hide the default Shopify login form
(function injectTempHideLoginCSS() {
  console.log("🎨 Injecting temporary hide login CSS...");
  if (document.getElementById('hyperotp-temp-hide-login-style')) return;
  const style = document.createElement("style");
  style.id = "hyperotp-temp-hide-login-style";
  style.innerHTML = `
    /* Hide all login/register forms IMMEDIATELY */
    .login-row,
    .login-rowItem,
    .login-rowLeft,
    .login-rowRight,
    .login-item,
    .halo-page-wrapper,
    form[action="/account/login"],
    form[action*="customer_login"],
    form[action*="create_customer"],
    form[action*="recover_customer_password"],
    .customer-login-form,
    .create-customer-form,
    #customer_login,
    .customer.login,
    [data-login-form],
    .shopify-section--main-password,
    .shopify-section--main-login,
    .section-header__title,
    .login-title,
    .form-title,
    .customer-login-title,
    #CustomerLoginForm,
    #RecoverPassword,
    #CreateCustomer,
    .account-form,
    .login-form,
    h1.title.row,
    .page-header,
    .account-page .halo-page-content .login-row,
    .account-page .halo-page-content .login-rowItem,
    .account-page .halo-page-content h2,
    .account-page .halo-page-content .login-text,
    .account-page .halo-page-content form,
    .account-page .halo-page-content .form-field,
    .account-page .halo-page-content .form-actions,
    .account-page .halo-page-content a[href="#recover"],
    .account-page .halo-page-content a[href="#login"],
    .account-page .breadcrumb,
    .account-page h1:not(#otp-login-container h1):not(.login-page-wrapper h1),
    .account-page h2:not(#otp-login-container h2):not(.login-page-wrapper h2) {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      left: -9999px !important;
      z-index: -1 !important;
    }
    
    /* Ensure OTP container is visible but don't break style tags */
    #otp-login-container,
    #otp-injection-point,
    .login-page-wrapper {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      position: relative !important;
      left: auto !important;
      z-index: auto !important;
      pointer-events: auto !important;
    }
    
    /* Keep style tags hidden (they should not display) */
    #otp-login-container style,
    #otp-injection-point style,
    .login-page-wrapper style {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
  console.log("✅ Temporary hide login CSS injected");
})();

// Inject placeholder protection styles with maximum specificity
(function injectPlaceholderProtectionCSS() {
  console.log("🛡️ Injecting placeholder protection CSS...");
  if (document.getElementById('hyperotp-placeholder-protection')) return;
  const style = document.createElement("style");
  style.id = "hyperotp-placeholder-protection";
  style.innerHTML = `
    /* Maximum specificity placeholder protection - must be loaded early */
    #otp-login-container input[name="identifier"]::placeholder,
    #otp-login-container .input-group input[name="identifier"]::placeholder,
    #otp-login-container .input-group input::placeholder,
    #otp-login-container input[name="otp"]::placeholder,
    #otp-login-container .otp-verify-input[name="otp"]::placeholder,
    #otp-login-container .otp-verify-form-row input[name="otp"]::placeholder,
    #otp-login-container input.otp-verify-input[name="otp"]::placeholder,
    #otp-login-container input[name="email"]::placeholder,
    #otp-login-container .email-input::placeholder,
    #otp-login-container .signup-input::placeholder,
    #otp-login-container .signup-input[type="email"]::placeholder,
    #otp-login-container input[name="first_name"]::placeholder,
    #otp-login-container input[name="last_name"]::placeholder,
    #otp-login-container .signup-form-row input::placeholder {
      color: #8A8A8A !important;
      opacity: 1 !important;
      font-family: 'Inter', Inter, sans-serif !important;
      font-size: inherit !important;
      letter-spacing: inherit !important;
      text-align: inherit !important;
      text-align-last: inherit !important;
      direction: inherit !important;
      -webkit-text-fill-color: #8A8A8A !important;
      -webkit-opacity: 1 !important;
    }
    
    /* Phone number placeholder specific */
    #otp-login-container input[name="identifier"]::placeholder,
    #otp-login-container .input-group input[name="identifier"]::placeholder {
      font-size: 16px !important;
      letter-spacing: normal !important;
    }
    
    /* OTP placeholder specific - always centered */
    #otp-login-container input[name="otp"]::placeholder,
    #otp-login-container .otp-verify-input[name="otp"]::placeholder,
    #otp-login-container .otp-verify-form-row input[name="otp"]::placeholder,
    #otp-login-container input.otp-verify-input[name="otp"]::placeholder {
      text-align: center !important;
      text-align-last: center !important;
      letter-spacing: normal !important;
      font-size: 20px !important;
    }
    
    /* Email placeholder specific */
    #otp-login-container input[name="email"]::placeholder,
    #otp-login-container .email-input::placeholder,
    #otp-login-container .signup-input[type="email"]::placeholder {
      font-size: 18px !important;
      letter-spacing: normal !important;
    }
    
    /* Signup input placeholders */
    #otp-login-container .signup-input::placeholder,
    #otp-login-container input[name="first_name"]::placeholder,
    #otp-login-container input[name="last_name"]::placeholder {
      font-size: 18px !important;
      letter-spacing: normal !important;
      color: #8A8A8A !important;
      opacity: 1 !important;
    }
    
    /* Mobile responsive placeholder protection */
    @media (max-width: 600px) {
      #otp-login-container .otp-verify-input[name="otp"]::placeholder,
      #otp-login-container input[name="otp"]::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
        text-align: center !important;
        text-align-last: center !important;
      }
      
      #otp-login-container .email-input::placeholder,
      #otp-login-container input[name="email"]::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
      }
    }
  `;
  document.head.appendChild(style);
  console.log("✅ Placeholder protection CSS injected");
})();

console.log("✅ login-override.js loaded");

/**
 * SECTION: Global Variables and Observers
 * Variables that need to be available before any functions are called
 */

/**
 * Observer to reapply placeholder protection when DOM changes
 */
let placeholderProtectionObserver = null;

function startPlaceholderProtectionObserver() {
  if (placeholderProtectionObserver) return;

  placeholderProtectionObserver = new MutationObserver((mutations) => {
    let shouldReapply = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' || mutation.type === 'attributes') {
        const container = document.getElementById('otp-login-container');
        if (container && (container.contains(mutation.target) || mutation.target === container)) {
          shouldReapply = true;
        }
      }
    });

    if (shouldReapply) {
      // Debounce reapplication
      clearTimeout(window._placeholderProtectionTimeout);
      window._placeholderProtectionTimeout = setTimeout(() => {
        applyPlaceholderProtection();
      }, 100);
    }
  });

  const container = document.getElementById('otp-login-container');
  if (container) {
    placeholderProtectionObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });
  }
}

/**
 * SECTION: Language Detection & Translation
 * Handles language detection and provides translations
 */

// Function to detect site language
function detectSiteLanguage() {
  // Check URL for Arabic locale indicators
  const pathname = window.location.pathname;
  const locales = ['ar-sa', 'ar-qa', 'ar-ae', 'ar-kw', 'ar-om', 'ar-bh'];

  for (const locale of locales) {
    if (pathname.includes(`/${locale}/`)) {
      return 'ar';
    }
  }

  // Check if URL starts with /ar/
  if (pathname.startsWith('/ar/') || pathname.includes('/ar-')) {
    return 'ar';
  }

  // Check HTML lang attribute
  const htmlLang = document.documentElement.lang || document.documentElement.getAttribute('lang');
  if (htmlLang && htmlLang.toLowerCase().startsWith('ar')) {
    return 'ar';
  }

  // Default to English
  return 'en';
}

// Translation object
const translations = {
  en: {
    loginSignup: 'Login/Signup',
    phoneNumber: 'Phone Number',
    getOTP: 'Get OTP',
    or: 'OR',
    loginEmailPassword: 'Login with Email & Password',
    poweredBy: 'Powered by Zoketo',
    enterOTP: 'Enter OTP',
    otpSentSuccess: 'OTP sent successfully on Phone, please enter it below',
    change: 'Change',
    submitOTP: 'Submit OTP',
    otpNotReceived: 'OTP not Received?',
    resendOTP: 'Resend OTP',
    enterPhoneNumber: 'Please enter a phone number.',
    validMobileNumber: 'Please enter a valid mobile number.',
    validUAEMobile: 'Please enter a valid UAE mobile number (9 digits, digits only).',
    validBahrainMobile: 'Please enter a valid Bahrain mobile number (8 digits, digits only).',
    validKuwaitMobile: 'Please enter a valid Kuwait mobile number (8 digits, digits only).',
    validOmanMobile: 'Please enter a valid Oman mobile number (8 digits, digits only).',
    validSaudiMobile: 'Please enter a valid Saudi Arabia mobile number (9 digits, digits only).',
    validQatarMobile: 'Please enter a valid Qatar mobile number (8 digits, digits only).',
    otpSent: 'OTP sent successfully.',
    failedSendOTP: 'Failed to send OTP.',
    validOTP: 'Please enter a valid 4-digit OTP.',
    enterEmail: 'Please enter your email.',
    enterOTPPlaceholder: 'Enter OTP',
    contactInformation: 'Contact Information',
    contactDescription: 'Please enter your email id to register and login',
    firstName: 'First name',
    firstNamePlaceholder: 'e.g John',
    lastName: 'Last name',
    lastNamePlaceholder: 'e.g Doe',
    emailAddress: 'Email Address',
    emailPlaceholder: 'e.g name@domain.com',
    subscribeNewsletter: 'Subscribe to newsletter and promotional offers',
    login: 'Login',
    continueBtn: 'Continue',
    sendOTP: 'Send OTP'
  },
  ar: {
    loginSignup: 'تسجيل الدخول بشكل OTP',
    phoneNumber: 'رقم التليفون',
    getOTP: 'إرسال رمز التحقق',
    or: 'أو تسجيل الدخول باستخدام',
    loginEmailPassword: 'تسجيل الدخول باستخدام البريد الإلكتروني',
    poweredBy: 'مدعوم من Zoketo',
    enterOTP: 'أدخل رمز التحقق',
    otpSentSuccess: 'تم إرسال رمز التحقق على رقم الهاتف',
    change: 'تغيير',
    submitOTP: 'تأكيد رمز التحقق',
    otpNotReceived: 'لم أتلق رمز التحقق',
    resendOTP: 'إعادة إرسال رمز التحقق',
    enterPhoneNumber: 'الرجاء إدخال رقم الهاتف.',
    validMobileNumber: 'الرجاء إدخال رقم هاتف محمول صالح.',
    validUAEMobile: 'الرجاء إدخال رقم هاتف محمول صالح في الإمارات (9 أرقام فقط).',
    validBahrainMobile: 'الرجاء إدخال رقم هاتف محمول صالح في البحرين (8 أرقام فقط).',
    validKuwaitMobile: 'الرجاء إدخال رقم هاتف محمول صالح في الكويت (8 أرقام فقط).',
    validOmanMobile: 'الرجاء إدخال رقم هاتف محمول صالح في عمان (8 أرقام فقط).',
    validSaudiMobile: 'الرجاء إدخال رقم هاتف محمول صالح في المملكة العربية السعودية (9 أرقام فقط).',
    validQatarMobile: 'الرجاء إدخال رقم هاتف محمول صالح في قطر (8 أرقام فقط).',
    otpSent: 'تم إرسال رمز التحقق بنجاح.',
    failedSendOTP: 'فشل في إرسال رمز التحقق.',
    validOTP: 'الرجاء إدخال رمز التحقق صالح مكون من 4 أرقام.',
    enterEmail: 'الرجاء إدخال بريدك الإلكتروني.',
    enterOTPPlaceholder: 'أدخل رمز التحقق',
    contactInformation: 'أدخل تفاصيل الحساب',
    contactDescription: 'الرجاء إدخال البريد الإلكتروني للتسجيل و الدخول',
    firstName: 'الإسم الأول',
    firstNamePlaceholder: 'مثال: محمد',
    lastName: 'إسم العائلة',
    lastNamePlaceholder: 'مثال: أحمد',
    emailAddress: 'عنوان البريد الإلكتروني',
    emailPlaceholder: 'مثال: name@domain.com',
    subscribeNewsletter: 'الإشتراك في النشرة الإخبارية والعروض الترويجية',
    login: 'تحديث',
    continueBtn: 'متابعة',
    sendOTP: 'إرسال رمز التحقق'
  }
};

// Get translation based on detected language
// Store in a closure to prevent shadowing
const translationFunction = function (key) {
  try {
    const lang = detectSiteLanguage();
    return translations[lang][key] || translations.en[key] || key;
  } catch (error) {
    console.error('Translation error:', error);
    return key; // Return key as fallback
  }
};

// Create t function that always works
function t(key) {
  return translationFunction(key);
}

// Store reference to translation function to prevent shadowing issues
// Use window object to ensure it's always accessible
window.hyperOTPTranslate = translationFunction;

// Create a safe translation function that can't be shadowed
const getTranslation = function (key) {
  try {
    if (typeof translationFunction === 'function') {
      return translationFunction(key);
    }
    if (typeof window.hyperOTPTranslate === 'function') {
      return window.hyperOTPTranslate(key);
    }
    // Fallback to direct translation lookup
    const lang = detectSiteLanguage();
    return translations[lang][key] || translations.en[key] || key;
  } catch (error) {
    console.error('Translation fallback error:', error);
    return key;
  }
};

// Check if current language is Arabic
function isArabic() {
  return detectSiteLanguage() === 'ar';
}

// Inject RTL styles for Arabic
function injectRTLStyles() {
  if (!isArabic()) return;

  if (document.getElementById('hyperotp-rtl-styles')) return;

  const style = document.createElement('style');
  style.id = 'hyperotp-rtl-styles';
  style.innerHTML = `
    /* RTL Support for Arabic */
    #otp-login-container {
      direction: rtl !important;
    }
    
    /* Input fields RTL alignment */
    #otp-login-container input[name="identifier"],
    #otp-login-container input[name="email"],
    #otp-login-container input[name="first_name"],
    #otp-login-container input[name="last_name"],
    .signup-input {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Signup inputs placeholder - right aligned */
    .signup-input::placeholder {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Signup inputs - full width and right aligned */
    .signup-input {
      width: 100% !important;
      box-sizing: border-box !important;
      text-align: right !important;
      direction: ltr !important;
    }
    
    /* Signup email input - stay right aligned while typing */
    .signup-input[type="email"],
    .signup-input[type="email"]:focus {
      text-align: right !important;
      direction: ltr !important;
    }
    
    /* Signup form row - full width */
    .signup-form-row {
      width: 100% !important;
    }
    
    /* Signup label - full width */
    .signup-label {
      width: 100% !important;
    }
    
    /* OTP input stays centered */
    #otp-login-container input[name="otp"],
    .otp-verify-input[name="otp"] {
      text-align: center !important;
      direction: ltr !important;
    }
    
    /* Remove extra spacing in input group fields */
    .input-group #otp-login-container input[name="identifier"],
    .input-group input[name="identifier"] {
      padding: 0 4px 0 0 !important;
      margin: 0 !important;
    }
    
    /* Phone number input specific - keep numbers LTR but align right */
    input[name="identifier"] {
      text-align: right !important;
      direction: ltr !important;
      unicode-bidi: plaintext !important;
      padding: 0 4px 0 0 !important;
      margin: 0 !important;
    }
    
    /* Email input specific - keep right aligned always */
    #otp-login-container input[type="email"],
    .email-input {
      direction: ltr !important;
      text-align: right !important;
    }
    
    /* Keep right aligned even when focused/typing */
    #otp-login-container input[type="email"]:focus,
    .email-input:focus {
      text-align: right !important;
    }
    
    /* Labels RTL alignment */
    .signup-label,
    .otp-verify-change-label,
    .otp-verify-bottom-label,
    .signup-checkbox-label {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Titles and descriptions RTL */
    .otp-verify-title,
    .otp-verify-desc,
    .signup-title,
    .signup-desc,
    #otp-login-container h2,
    .otp-title {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Main page title specific */
    #otp-login-container > h2:first-of-type {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Message box RTL */
    #otp-message {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Phone input group RTL layout */
    .input-group {
      direction: ltr !important;
      display: flex !important;
      flex-direction: row-reverse !important;
      justify-content: flex-start !important;
      gap: 0 !important;
      padding: 0 12px !important;
    }
    
    /* Country code select - appears on RIGHT in Arabic */
    .input-group select,
    #country-code {
      order: 1 !important;
      text-align: center !important;
      direction: ltr !important;
      padding: 0 4px 0 0 !important;
      margin: 0 !important;
      flex-shrink: 0 !important;
      border: none !important;
    }
    
    /* Phone input - appears on LEFT in Arabic */
    .input-group input,
    .input-group input[name="identifier"] {
      order: 2 !important;
      flex: 1 !important;
      text-align: right !important;
      direction: ltr !important;
      padding: 0 4px 0 0 !important;
      margin: 0 !important;
      border: none !important;
    }
    
    /* OTP input - keep centered */
    .otp-verify-input[name="otp"] {
      text-align: center !important;
      direction: ltr !important;
    }
    
    /* OTP input placeholder - center aligned for Arabic */
    #otp-login-container .otp-verify-input[name="otp"]::placeholder,
    .otp-verify-form-row input[name="otp"]::placeholder {
      text-align: center !important;
      text-align-last: center !important;
      direction: rtl !important;
    }
    
    /* Change and resend links RTL */
    .otp-verify-bottom-row {
      direction: rtl !important;
      justify-content: flex-start !important;
      text-align: right !important;
    }
    
    .otp-verify-bottom-link,
    .otp-verify-change-label {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Checkbox wrapper RTL */
    .signup-checkbox-wrapper {
      direction: rtl !important;
      flex-direction: row-reverse !important;
      justify-content: flex-start !important;
      text-align: right !important;
    }
    
    .signup-checkbox {
      order: 2 !important;
      margin-right: 0 !important;
      margin-left: 12px !important;
    }
    
    .signup-checkbox-label {
      order: 1 !important;
      flex: 1 !important;
    }
    
    /* Form rows RTL */
    .signup-form-row,
    .otp-verify-form-row {
      direction: rtl !important;
    }
    
    /* Signup form row - align right for Arabic */
    .signup-form-row {
      text-align: right !important;
    }
    
    /* Signup form row with checkbox - align right */
    .signup-form-row:has(.signup-checkbox-wrapper) {
      align-items: flex-start !important;
    }
    
    /* Buttons - keep centered text */
    .otp-btn,
    .otp-verify-btn,
    .signup-btn,
    .email-btn,
    .otp-email-btn {
      direction: rtl !important;
    }
    
    /* Social buttons - keep centered */
    .social-btn,
    .otp-social-btn {
      direction: ltr !important;
    }
    
    /* Powered by text - align right in Arabic */
    #otp-login-container > div[style*="font-size: 10px"] {
      text-align: left !important;
      direction: rtl !important;
    }
    
    /* Divider text */
    .divider,
    .otp-divider {
      direction: rtl !important;
    }
    
    /* Placeholders RTL */
    #otp-login-container input::placeholder {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* Phone number placeholder in input-group - right aligned */
    .input-group input::placeholder,
    .input-group input[name="identifier"]::placeholder {
      text-align: right !important;
      direction: rtl !important;
      padding: 0 !important;
      margin: 0 !important;
    }
    
    /* Email placeholder - right aligned for Arabic text */
    #otp-login-container input[type="email"]::placeholder,
    .email-input::placeholder,
    .signup-input[type="email"]::placeholder {
      text-align: right !important;
      direction: rtl !important;
    }
    
    /* OTP placeholder - OVERRIDE to center (most specific) */
    #otp-login-container input[name="otp"]::placeholder,
    #otp-login-container .otp-verify-input[name="otp"]::placeholder,
    .otp-verify-form-row input[name="otp"]::placeholder,
    input.otp-verify-input[name="otp"]::placeholder {
      text-align: center !important;
      text-align-last: center !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  `;
  document.head.appendChild(style);
  console.log("✅ RTL styles injected for Arabic language");
}

/**
 * SECTION: Initial Setup
 * Injects required scripts and sets up the base configuration
 */

// Function to check which social logins are enabled
function getEnabledSocialLogins() {
  if (!window.hyperOTPSettings || !window.hyperOTPSettings.socialLogins || window.hyperOTPSettings.socialLogins.trim() === '') {
    return { google: false, facebook: false };
  }
  const socialLogins = window.hyperOTPSettings.socialLogins.toLowerCase().split(',');
  const result = {
    google: socialLogins.includes('google'),
    facebook: socialLogins.includes('facebook')
  };
  return result;
}

// Function to check if OTP form should be shown
function shouldShowOTPForm() {
  if (!window.hyperOTPSettings) return true;
  const method = window.hyperOTPSettings.loginMethod;
  return method === "ONLY_PHONE" || method === "EMAIL_AND_PHONE";
}

// Function to check if email password login should be shown
function shouldShowEmailPasswordLogin() {
  if (!window.hyperOTPSettings) return true;
  const method = window.hyperOTPSettings.loginMethod;
  return method === "ONLY_EMAIL_PASSWORD" || method === "EMAIL_AND_PHONE";
}

// Function to get country display name from phone number
function getCountryDisplayName(phone) {
  if (!phone || typeof phone !== 'string') return '';

  if (phone.startsWith('+971')) return 'UAE';
  if (phone.startsWith('+973')) return 'BH';
  if (phone.startsWith('+965')) return 'KW';
  if (phone.startsWith('+968')) return 'OM';
  if (phone.startsWith('+966')) return 'SA';
  if (phone.startsWith('+974')) return 'QA';

  return '';
}

// Function to format phone number for display
function formatPhoneDisplay(phone) {
  if (!phone || typeof phone !== 'string') return '';

  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If it starts with +, format it nicely
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.substring(0, 4); // +971, +973, etc.
    const number = cleaned.substring(4);

    // Format based on country code
    if (countryCode === '+971') {
      // UAE: +971 50 123 4567
      if (number.length >= 9) {
        return `+971 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    } else if (countryCode === '+973') {
      // Bahrain: +973 1234 5678
      if (number.length >= 8) {
        return `+973 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    } else if (countryCode === '+965') {
      // Kuwait: +965 1234 5678
      if (number.length >= 8) {
        return `+965 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    } else if (countryCode === '+968') {
      // Oman: +968 1234 5678
      if (number.length >= 8) {
        return `+968 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    } else if (countryCode === '+966') {
      // Saudi Arabia: +966 50 123 4567
      if (number.length >= 9) {
        return `+966 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
    } else if (countryCode === '+974') {
      // Qatar: +974 1234 5678
      if (number.length >= 8) {
        return `+974 ${number.substring(0, 4)} ${number.substring(4)}`;
      }
    }

    // Default formatting for other countries
    return cleaned;
  }

  return cleaned;
}

// Function to extract phone number without country code
function extractPhoneWithoutCountryCode(phone) {
  if (!phone || typeof phone !== 'string') return '';

  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If it starts with +, remove the country code
  if (cleaned.startsWith('+')) {
    return cleaned.substring(4); // Remove the country code (+971, +973, etc.)
  }

  return cleaned;
}

// Function to get country code from phone number
function getCountryCodeFromPhone(phone) {
  if (!phone || typeof phone !== 'string') return '+971'; // Default to UAE

  // Remove any non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If it starts with +, extract the country code
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.substring(0, 4); // +971, +973, etc.
    return countryCode;
  }

  return '+971'; // Default to UAE
}

// Function to generate country options based on countryCode setting
function generateCountryOptions() {
  const settings = window.hyperOTPSettings;
  const countryCode = settings?.countryCode || 'ALL';

  const allCountries = [
    { code: '+971', name: 'UAE' },
    { code: '+973', name: 'BH' },
    { code: '+965', name: 'KW' },
    { code: '+968', name: 'OM' },
    { code: '+966', name: 'SA' },
    { code: '+974', name: 'QA' }
  ];

  // If countryCode is 'ALL', return all countries
  if (countryCode === 'ALL') {
    return allCountries;
  }

  // If countryCode is a specific country, return only that country
  const countryMap = {
    'UAE': { code: '+971', name: 'UAE' },
    'BH': { code: '+973', name: 'BH' },
    'KW': { code: '+965', name: 'KW' },
    'OM': { code: '+968', name: 'OM' },
    'SA': { code: '+966', name: 'SA' },
    'QA': { code: '+974', name: 'QA' }
  };

  return countryMap[countryCode] ? [countryMap[countryCode]] : allCountries;
}

// Function to fetch geo-location and set country code
// REFACTORED: Now uses Liquid injection only, no external API call
function fetchGeoLocation(container) {
  // If no container provided, try to find one
  if (!container) {
    container = document.getElementById('otp-login-container') ||
      document.getElementById('otp-sidebar-container');
  }

  if (!container) {
    console.log("ZoketoOTP: No OTP container found for geo-location");
    return;
  }

  // If a phone number is already entered (e.g. prefilled), do not override
  const identifierInput = container.querySelector('input[name="identifier"]');
  if (identifierInput && identifierInput.value && identifierInput.value.trim().length > 0) return;

  const setCode = (c) => {
    const countryOptions = generateCountryOptions();
    // Check if the country code exists in our allowed options
    const isValid = countryOptions.some(opt => opt.code === c);

    if (isValid) {
      const sel = container.querySelector('#country-code');
      if (sel) sel.value = c;
    }
  };

  const isoMap = {
    'AE': '+971', 'BH': '+973', 'KW': '+965',
    'OM': '+968', 'SA': '+966', 'QA': '+974'
  };

  // Check if country code is available from Liquid injection
  if (window.hyperOTPUserData && window.hyperOTPUserData.countryCode) {
    const injectedCode = window.hyperOTPUserData.countryCode;
    console.log("ZoketoOTP: CountryCode::", injectedCode);
    if (injectedCode && isoMap[injectedCode]) {
      setCode(isoMap[injectedCode]);
    }
  } else {
    // Fallback or log if missing
    console.log("ZoketoOTP: No country code found in Liquid data");
  }
}

// Function to generate social login buttons HTML based on settings
function generateSocialButtonsHTML() {
  const { google, facebook } = getEnabledSocialLogins();
  if (!google && !facebook) return '';

  let html = '<div class="otp-social-row">';

  if (google) {
    html += `
      <!-- Server-side Google SSO Button -->
      <button type="button" class="otp-social-btn google-sso-btn" onclick="event.preventDefault(); var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com'; window.open('https://otp.zoketo.com/app/authgoogle?shop=' + encodeURIComponent(shop), 'googleSSO', 'width=500,height=600'); return false;">
        <img src='https://img.icons8.com/color/48/google-logo.png' alt='Google' />
      </button>
    `;
  }

  if (facebook) {
    html += `
      <!-- Server-side Facebook SSO Button -->
      <button type="button" class="otp-social-btn social-btn facebook-sso-btn">
        <img src='https://img.icons8.com/fluency/48/facebook-new.png' alt='Facebook' />
        <span style="font-size:0;">Facebook</span>
      </button>
    `;
  }

  html += '</div>';
  return html;
}

// Function to fetch settings from app proxy
async function fetchHyperOTPSettings() {
  try {
    // Check if settings are available from window object (from login-embed.liquid)
    // Only use metafield settings if they exist and are not empty
    if (window.hyperOTPUserData &&
      window.hyperOTPUserData.settings &&
      typeof window.hyperOTPUserData.settings === 'object' &&
      window.hyperOTPUserData.settings !== null &&
      Object.keys(window.hyperOTPUserData.settings).length > 0) {
      window.hyperOTPSettings = window.hyperOTPUserData.settings;
      console.log("✅ HyperOTP Settings loaded from metafield:", window.hyperOTPUserData.settings);
      return window.hyperOTPUserData.settings;
    }

    // Fallback to API call if window object is not available
    console.log("⚠️ Settings not found in window object, falling back to API call");
    const res = await fetch(getAppProxyUrl(), {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ step: "settings" })
    });
    const result = await res.json();
    if (result.success) {
      window.hyperOTPSettings = result.settings;
      console.log("✅ HyperOTP Settings loaded from API:", result.settings);
      return result.settings;
    } else {
      console.error("❌ Failed to load settings:", result.error);
      return null;
    }
  } catch (error) {
    console.error("❌ Failed to load settings:", error);
    return null;
  }
}


// Build absolute App Proxy URL with full path including locale prefix
function getAppProxyUrl() {
  try {
    // Get the current origin and pathname
    const origin = window.location.origin;
    const pathname = window.location.pathname;

    // Check for specific locale prefixes
    const locales = ['en-sa', 'en-qa', 'en-ae', 'ar-sa', 'ar-qa', 'ar-ae', 'en-kw', 'en-om', 'en-bh', 'sa-kw', 'sa-om', 'sa-bh'];
    let localePrefix = '';

    for (const locale of locales) {
      if (pathname.includes(`/${locale}/`)) {
        localePrefix = `/${locale}`;
        break;
      }
    }

    const url = origin + localePrefix + '/apps/zoketootp';
    //console.log("Proxy URL built:", url, "from pathname:", pathname);
    //console.log("Proxy FinalURL built:", url, "from pathname:", url);
    return url;
  } catch (_) {
    // Fallback to root-absolute if URL parsing fails
    return '/apps/zoketootp';
  }
}

// Inject Google SSO script
(function injectGoogleSSOScript() {
  if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
    const script = document.createElement('script');
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }
})();

/**
 * SECTION: Main Login UI Setup
 * Sets up the login interface and handles form visibility
 */
// Start fetching settings as soon as script loads
let hyperOTPSettingsPromise = fetchHyperOTPSettings();

// Function to detect Shopify login errors
function isShopifyLoginErrorPresent() {
  // Check for common Shopify error selectors
  const errorSelectors = [
    '.form-message', // Shopify Dawn theme
    '.errors',       // Older themes
    '.form__message', // Some other themes
  ];
  return errorSelectors.some(sel => {
    const el = document.querySelector(sel);
    return el && el.textContent && el.textContent.trim().length > 0;
  });
}

/**
 * Check if current page is the login page
 * @returns {boolean} True if on /account/login page
 */
function isLoginPage() {
  const path = window.location.pathname;
  // Check for /account/login or locale-prefixed versions
  return path === '/account/login' ||
    path.endsWith('/account/login') ||
    path.includes('/account/login');
}

/**
 * Check if user is logged in
 * @returns {boolean} True if user is logged in
 */
function isUserLoggedIn() {
  // Check for Shopify customer object
  if (window.Shopify && window.Shopify.customer) {
    return true;
  }

  // Check for account dropdown in sidebar (indicates logged in)
  const accountDropdown = document.querySelector('.account-dropdown');
  if (accountDropdown) {
    return true;
  }

  // Check if we're on /account page (not /account/login)
  const path = window.location.pathname;
  if (path === '/account' || path.endsWith('/account')) {
    return true;
  }

  return false;
}

/**
 * Check if the login page has the correct login forms
 * If not, clear the wrong content and prepare for OTP injection
 * @returns {boolean} True if forms exist or were fixed
 */
function ensureLoginPageStructure() {
  // Check if we're on login page
  if (!isLoginPage()) {
    return false;
  }

  // Check if login forms exist
  const hasLoginForm = document.querySelector('.customer-login-form') ||
    document.querySelector('form[action*="customer_login"]') ||
    document.querySelector('#CustomerLoginForm');

  const hasRegisterForm = document.querySelector('.create-customer-form') ||
    document.querySelector('form[action*="create_customer"]');

  // If forms exist, we're good
  if (hasLoginForm || hasRegisterForm) {
    console.log('✅ Login forms detected');
    return true;
  }

  // Forms don't exist - likely wrong template content
  console.warn('⚠️ Login forms not found - clearing page content for OTP injection');

  // Try to find the most specific content container first
  let targetElement = document.querySelector('.halo-page-content');

  // If not found, try other selectors
  if (!targetElement) {
    const selectors = ['.account-page', 'main', '#MainContent', '.container'];
    for (const selector of selectors) {
      targetElement = document.querySelector(selector);
      if (targetElement) break;
    }
  }

  if (targetElement) {
    // Clear the wrong content
    targetElement.innerHTML = '';
    console.log(`🧹 Cleared wrong content from page`);

    // Add a simple wrapper for OTP with proper styling
    targetElement.innerHTML = `
      <div class="login-page-wrapper" style="max-width: 600px; margin: 60px auto; padding: 20px;">
        <h1 style="text-align: center; margin-bottom: 40px; font-size: 32px; font-weight: 700; color: #222;">Login</h1>
        <div id="otp-injection-point"></div>
      </div>
    `;
    console.log('✅ Created OTP injection point');
    return true;
  }

  console.error('❌ Could not find suitable container to clear');
  return false;
}

// Function to find the best container for OTP form injection
function findBestContainer() {
  // Priority order for finding the best container
  const selectors = [
    '#otp-injection-point', // Created by ensureLoginPageStructure if forms are missing
    'main',
    '#MainContent',
    '.container',
    '.login-form',
    '.row',
    '#CustomerLoginForm',
    '.account-form',
    'body'
  ];

  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element) {
      // If we found .login-form, try to get its parent container
      if (selector === '.login-form') {
        return element.parentElement?.parentElement || element.parentElement || element;
      }
      // If we found #CustomerLoginForm, try to get its parent container
      if (selector === '#CustomerLoginForm') {
        return element.parentElement || element;
      }
      return element;
    }
  }

  return document.body;
}

// Fallback for when DOM is already loaded
if (document.readyState === 'loading') {
  console.log("📋 DOM still loading - waiting for DOMContentLoaded...");
} else {
  console.log("📋 DOM already loaded - executing initialization immediately...");
  // Execute the initialization logic immediately
  (function initializeOTP() {
    console.log("📅 DOMContentLoaded event fired - starting main initialization...");

    // Inject RTL styles if Arabic
    injectRTLStyles();

    // Check if we should render OTP on this page
    if (!isLoginPage()) {
      console.log('ℹ️ Not on login page, skipping OTP rendering');
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      return;
    }

    // Check if user is already logged in
    if (isUserLoggedIn()) {
      console.log('ℹ️ User is logged in, skipping OTP rendering');
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      return;
    }

    // Ensure login page has correct structure (fix if Liquid template is wrong)
    ensureLoginPageStructure();

    // If Shopify login error is present, do not inject OTP UI
    if (isShopifyLoginErrorPresent()) {
      console.log("⚠️ Shopify login error detected - not injecting OTP UI");
      // Optionally, remove any temp CSS you injected
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      return;
    }
    // 1. Render the OTP form immediately with loading state
    console.log('🎯 Starting OTP form rendering...');
    if (!document.getElementById('otp-login-container')) {
      console.log('📦 Creating OTP container...');
      const otpContainer = document.createElement("div");
      otpContainer.id = "otp-login-container";
      otpContainer.className = "otp-fade-in";
      otpContainer.style.cssText = `
        background: #fff;
        max-width: 470px;
        margin: 40px auto;
        border-radius: 16px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.07);
        padding: 12px 24px 24px 24px;
        font-family: 'Inter', sans-serif;
        opacity: 0;
        transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
       
      `;
      renderInitialForm(otpContainer, { loadingSocial: true });
      // Use the new function to find the best container
      const container = findBestContainer();
      console.log('📍 Injecting OTP into container:', container);
      container.appendChild(otpContainer);
      setTimeout(() => {
        otpContainer.style.opacity = '1';
        console.log('✅ OTP form visible');
        // Apply placeholder protection after container is visible
        applyPlaceholderProtection(otpContainer);
        startPlaceholderProtectionObserver();
      }, 10);
    } else {
      console.log('ℹ️ OTP container already exists');
    }

    // Remove loader if present
    const loader = document.getElementById('otp-loader');
    if (loader) loader.remove();

    // When settings are ready, update the UI
    hyperOTPSettingsPromise.then(settings => {
      window.hyperOTPSettings = settings;
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      const otpContainer = document.getElementById('otp-login-container');
      if (!settings || settings.appEnabled === false) {
        // Remove the temp CSS so default login shows
        if (tempStyle) tempStyle.remove();
        if (otpContainer) otpContainer.remove();
        return;
      }
      if (settings.loginMethod === 'ONLY_EMAIL_PASSWORD') {
        if (tempStyle) tempStyle.remove();
        showShopifyDefaultLoginForm();
        return;
      }
      // Apply dynamic button colors based on settings
      applyDynamicButtonColors();
      // Now update the OTP UI with real settings
      if (otpContainer) {
        renderInitialForm(otpContainer, { loadingSocial: false });
        const { google, facebook } = getEnabledSocialLogins();
        const showOTPForm = shouldShowOTPForm();
        const showEmailPassword = shouldShowEmailPasswordLogin();
        const googleButton = otpContainer.querySelector('.google-sso-btn');
        const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
        const emailButton = otpContainer.querySelector('.email-btn');
        const divider = otpContainer.querySelector('.divider');
        const otpFormElement = otpContainer.querySelector('#otp-form');
        if (googleButton) {
          if (google) {
            googleButton.disabled = false;
            googleButton.style.opacity = '1';
          } else {
            googleButton.style.display = 'none';
          }
        }
        if (facebookButton) {
          if (facebook) {
            facebookButton.disabled = false;
            facebookButton.style.opacity = '1';
          } else {
            facebookButton.style.display = 'none';
          }
        }
        if (emailButton) {
          if (showEmailPassword) {
            emailButton.disabled = false;
            emailButton.style.opacity = '1';
          } else {
            emailButton.style.display = 'none';
          }
        }
        if (otpFormElement) {
          otpFormElement.style.display = showOTPForm ? 'block' : 'none';
        }
        if (divider && !google && !facebook && !showEmailPassword) {
          divider.style.display = 'none';
        }
        attachGoogleSSOHandlers();
        attachFacebookSSOHandlers();
      }
      injectHideLoginCSS();
      if (tempStyle) tempStyle.remove();
    });
  })();
}

document.addEventListener("DOMContentLoaded", () => {
  console.log("📅 DOMContentLoaded event fired - starting main initialization...");

  // Inject RTL styles if Arabic
  injectRTLStyles();

  // Check if we should render OTP on this page
  if (!isLoginPage()) {
    console.log('ℹ️ Not on login page, skipping OTP rendering');
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    return;
  }

  // Check if user is already logged in
  if (isUserLoggedIn()) {
    console.log('ℹ️ User is logged in, skipping OTP rendering');
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    return;
  }

  // Ensure login page has correct structure (fix if Liquid template is wrong)
  ensureLoginPageStructure();

  // If Shopify login error is present, do not inject OTP UI
  if (isShopifyLoginErrorPresent()) {
    console.log("⚠️ Shopify login error detected - not injecting OTP UI");
    // Optionally, remove any temp CSS you injected
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    return;
  }
  // 1. Render the OTP form immediately with loading state
  console.log('🎯 Starting OTP form rendering...');
  if (!document.getElementById('otp-login-container')) {
    console.log('📦 Creating OTP container...');
    const otpContainer = document.createElement("div");
    otpContainer.id = "otp-login-container";
    otpContainer.className = "otp-fade-in";
    otpContainer.style.cssText = `
      background: #fff;
      max-width: 470px;
      margin: 40px auto;
      border-radius: 16px;
      box-shadow: 0 2px 12px rgba(0,0,0,0.07);
      padding: 12px 24px 24px 24px;
      font-family: 'Inter', sans-serif;
      opacity: 0;
      transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
     
    `;
    renderInitialForm(otpContainer, { loadingSocial: true });
    // Use the new function to find the best container
    const container = findBestContainer();
    console.log('📍 Injecting OTP into container:', container);
    container.appendChild(otpContainer);
    setTimeout(() => {
      otpContainer.style.opacity = '1';
      console.log('✅ OTP form visible');
      // Apply placeholder protection after container is visible
      applyPlaceholderProtection(otpContainer);
      startPlaceholderProtectionObserver();
    }, 10);
  } else {
    console.log('ℹ️ OTP container already exists');
  }

  // Remove loader if present
  const loader = document.getElementById('otp-loader');
  if (loader) loader.remove();

  // When settings are ready, update the UI
  hyperOTPSettingsPromise.then(settings => {
    window.hyperOTPSettings = settings;
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    const otpContainer = document.getElementById('otp-login-container');
    if (!settings || settings.appEnabled === false) {
      // Remove the temp CSS so default login shows
      if (tempStyle) tempStyle.remove();
      if (otpContainer) otpContainer.remove();
      return;
    }
    if (settings.loginMethod === 'ONLY_EMAIL_PASSWORD') {
      if (tempStyle) tempStyle.remove();
      showShopifyDefaultLoginForm();
      return;
    }
    // Apply dynamic button colors based on settings
    applyDynamicButtonColors();
    // Now update the OTP UI with real settings
    if (otpContainer) {
      renderInitialForm(otpContainer, { loadingSocial: false });
      const { google, facebook } = getEnabledSocialLogins();
      const showOTPForm = shouldShowOTPForm();
      const showEmailPassword = shouldShowEmailPasswordLogin();
      const googleButton = otpContainer.querySelector('.google-sso-btn');
      const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
      const emailButton = otpContainer.querySelector('.email-btn');
      const divider = otpContainer.querySelector('.divider');
      const otpFormElement = otpContainer.querySelector('#otp-form');
      if (googleButton) {
        if (google) {
          googleButton.disabled = false;
          googleButton.style.opacity = '1';
        } else {
          googleButton.style.display = 'none';
        }
      }
      if (facebookButton) {
        if (facebook) {
          facebookButton.disabled = false;
          facebookButton.style.opacity = '1';
        } else {
          facebookButton.style.display = 'none';
        }
      }
      if (emailButton) {
        if (showEmailPassword) {
          emailButton.disabled = false;
          emailButton.style.opacity = '1';
        } else {
          emailButton.style.display = 'none';
        }
      }
      if (otpFormElement) {
        otpFormElement.style.display = showOTPForm ? 'block' : 'none';
      }
      if (divider && !google && !facebook && !showEmailPassword) {
        divider.style.display = 'none';
      }
      attachGoogleSSOHandlers();
      attachFacebookSSOHandlers();
    }
    injectHideLoginCSS();
    if (tempStyle) tempStyle.remove();
  });
});

/**
 * SECTION: OTP Form Handler
 * Manages the OTP form submission, verification, and user registration process
 * @param {HTMLFormElement} form - The OTP form element
 * @param {HTMLElement} otpContainer - The container element for the OTP UI
 */
function attachOtpFormHandler(form, otpContainer) {
  const stepInput = form.querySelector('input[name="step"]');
  const identifierInput = form.querySelector('input[name="identifier"]');
  const countryCodeSelect = form.querySelector('#country-code');
  const otpInputWrapper = form.querySelector("#otp-input-wrapper");
  const otpInput = form.querySelector('input[name="otp"]');
  const nameInputWrapper = form.querySelector("#name-input-wrapper");
  const emailInputWrapper = form.querySelector("#email-input-wrapper");
  const firstNameInput = form.querySelector('input[name="first_name"]');
  const lastNameInput = form.querySelector('input[name="last_name"]');
  const emailInput = form.querySelector('input[name="email"]');
  const messageBox = document.getElementById("otp-message");
  const submitButton = form.querySelector("button[type='submit']");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    // Use safe translation reference to prevent shadowing issues (some stores have global 't' variable)
    const translate = window.hyperOTPTranslate || translationFunction || getTranslation;

    // Fetch settings if not already loaded
    if (!window.hyperOTPSettings) {
      await fetchHyperOTPSettings();
    }

    const step = stepInput ? stepInput.value : "send";
    let identifier = identifierInput ? identifierInput.value.trim() : "";
    let countryCode = countryCodeSelect ? countryCodeSelect.value : "";
    // Remove all spaces from identifier before any further processing (for phone only)
    if (identifier && !identifier.includes("@")) {
      identifier = identifier.replace(/\s+/g, "");
    }
    // Only concatenate if not email and not already has country code
    if (identifier && countryCode && !identifier.startsWith("+") && !identifier.includes("@")) {
      identifier = countryCode + identifier;
    }
    const otp = otpInput?.value?.trim();

    // Phone validation for GCC countries
    function isValidGCCPhone(phone) {
      if (typeof phone !== 'string') return false;

      // UAE: +971XXXXXXXXX (9 digits after +971)
      if (phone.startsWith('+971')) {
        const number = phone.slice(4);
        return /^\d{9}$/.test(number);
      }

      // Bahrain: +973XXXXXXXX (8 digits after +973)
      if (phone.startsWith('+973')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      // Kuwait: +965XXXXXXXX (8 digits after +965)
      if (phone.startsWith('+965')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      // Oman: +968XXXXXXXX (8 digits after +968)
      if (phone.startsWith('+968')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      // Saudi Arabia: +966XXXXXXXX (8 digits after +966)
      if (phone.startsWith('+966')) {
        const number = phone.slice(4);
        return /^\d{9}$/.test(number);
      }

      // Qatar: +974XXXXXXXX (8 digits after +974)
      if (phone.startsWith('+974')) {
        const number = phone.slice(4);
        return /^\d{8}$/.test(number);
      }

      return true; // For other countries, don't validate here
    }

    if (!identifier) {
      if (messageBox) {
        messageBox.style.color = "red";
        messageBox.innerText = translate('enterPhoneNumber');
      }
      return;
    }
    // GCC phone validation (only for phone, not email)
    if (!identifier.includes("@") && !isValidGCCPhone(identifier)) {
      if (messageBox) {
        const countryCode = identifier.startsWith('+') ? identifier.substring(0, 4) : '';
        let errorMessage = translate('validMobileNumber');

        if (countryCode === "+971") {
          errorMessage = translate('validUAEMobile');
        } else if (countryCode === "+973") {
          errorMessage = translate('validBahrainMobile');
        } else if (countryCode === "+965") {
          errorMessage = translate('validKuwaitMobile');
        } else if (countryCode === "+968") {
          errorMessage = translate('validOmanMobile');
        } else if (countryCode === "+966") {
          errorMessage = translate('validSaudiMobile');
        } else if (countryCode === "+974") {
          errorMessage = translate('validQatarMobile');
        }

        messageBox.style.color = "red";
        messageBox.innerText = errorMessage;
      }
      return;
    }

    if (step === "send") {
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      // console.log("PRoxy URL::", getAppProxyUrl());
      const res = await fetch(getAppProxyUrl(), {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: identifier, step: "send" })
      });
      const result = await res.json();
      // console.log("[OTP]", result.otp);
      // console.log("[SEND OTP RESPONSE]", result);
      submitButton.disabled = false;

      if (result.success) {
        if (messageBox) {
          messageBox.innerText = result.message || translate('otpSent');
          messageBox.style.color = "green";
        }
        // Get enabled social logins and check if OTP form should be shown
        const { google, facebook } = getEnabledSocialLogins();
        const showSocialDivider = google || facebook;
        const showOTPForm = shouldShowOTPForm();
        const showEmailPassword = shouldShowEmailPasswordLogin();

        if (!showOTPForm) {
          // If OTP form should not be shown, just show social buttons and email password if enabled
          // Use safe translation reference in template string - use window.hyperOTPTranslate directly to avoid shadowing
          const translateSafe = window.hyperOTPTranslate || translationFunction || getTranslation;
          otpContainer.innerHTML = `
            <style>
              // ... existing styles ...
            </style>
            <h2 class="otp-title" style="color: ${window.hyperOTPSettings?.loginHeaderColor || 'white'}; font-size: 22px;">${isArabic() ? 'تسجيل الدخول باستخدام OTP' : (window.hyperOTPSettings?.loginHeader || 'Login/Signup')}</h2>
            <div id="otp-message" style="margin-bottom: 1rem; color: green;"></div>
            ${generateSocialButtonsHTML()}
            ${showEmailPassword ? `<button type="button" class="otp-email-btn">${translateSafe('loginEmailPassword')}</button>` : ''}
            ${!shouldHidePoweredBy() ? `<div style="font-size: 10px; text-align: right; padding-top: 8px;">${translateSafe('poweredBy')}</div>` : ''}
          `;
          return;
        }

        // Custom OTP UI for verify step
        const showEmailPassword2 = shouldShowEmailPasswordLogin();
        // Inject RTL styles if Arabic
        injectRTLStyles();
        // Use safe translation reference in template string - use window.hyperOTPTranslate directly to avoid shadowing
        const translate = window.hyperOTPTranslate || translationFunction || getTranslation;
        otpContainer.innerHTML = `
            <style>
              .otp-verify-title { font-family: Inter, sans-serif; font-size: 30px; font-weight: 700; color: #000; text-align: center; margin-top: 0; margin-bottom: 15px; }
              .otp-verify-desc { 
                 text-align: left; 
                color: #000000; 
                margin-bottom: -11px; 
                font-size: 13px; 
                font-family: "Inter", sans-serif;
                font-optical-sizing: auto;
                font-weight: 215;
                font-style: normal;
              }
              .otp-verify-row { display: flex; align-items: center; justify-content: flex-start; gap: 8px; margin-bottom: 1.2rem; }
              .otp-verify-in { 
               text-align: left; color: #000; margin-bottom: 0.8rem; font-weight: 700; font-size: 16px; font-family: Inter, sans-serif;  }
              .otp-verify-change { font-family: Inter, sans-serif; font-size: 18px;  font-weight: 450; color: #005BD3; text-decoration: underline; cursor: pointer;  }
              .otp-verify-form-row { display: flex; flex-direction: row; align-items: left; gap: 4px; width: 100%; margin-bottom: 1.5rem; }
              .otp-verify-input { flex: 1; height: 45px; margin-top: 0; padding: 0 8px; border: 0.66px solid #8A8A8A; background: #FDFDFD; font-size: 32px; color: #303030; font-family: Inter, sans-serif; border-radius: 8px; outline: none; text-align: center; box-shadow: none !important; letter-spacing: 1.2em; box-sizing: border-box; max-width: 100%; width: 100%; line-height: 45px; }
              .otp-verify-input::placeholder { 
                letter-spacing: normal !important; 
                font-size: 20px !important; 
                text-align: center !important; 
                text-align-last: center !important; 
                color: #8A8A8A !important;
                opacity: 1 !important;
                font-family: 'Inter', Inter, sans-serif !important;
                text-indent: 0 !important;
                display: inline-block !important;
                width: 100% !important;
              }
              .otp-verify-input[name="otp"]::placeholder { 
                text-align: center !important; 
                text-align-last: center !important; 
                letter-spacing: normal !important;
                font-size: 20px !important;
                color: #8A8A8A !important;
                opacity: 1 !important;
                text-indent: 0 !important;
                padding-left: 0 !important;
                padding-right: 0 !important;
                line-height: 45px !important;
              }
              /* Separate CSS for Enter OTP placeholder in verification form */
              #otp-input-wrapper .otp-verify-input[name="otp"]::placeholder {
                text-align: center !important;
                text-align-last: center !important;
                letter-spacing: normal !important;
                font-size: 20px !important;
                color: #8A8A8A !important;
                opacity: 1 !important;
                line-height: 45px !important;
                vertical-align: middle !important;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
                margin: 0 !important;
              }
              
              /* NEW: Dedicated CSS for Enter OTP input field */
              .enterotp-verify-input { 
                flex: 1; 
                height: 45px; 
                margin-top: 0; 
                padding: 0 8px; 
                border: 0.66px solid #8A8A8A; 
                background: #FDFDFD; 
                font-size: 32px; 
                color: #303030; 
                font-family: Inter, sans-serif; 
                border-radius: 8px; 
                outline: none; 
                text-align: center; 
                box-shadow: none !important; 
                letter-spacing: 1.2em; 
                box-sizing: border-box; 
                max-width: 100%; 
                width: 100%; 
                line-height: 45px;
                padding-bottom: 10px !important;
              }
              
              /* NEW: Dedicated placeholder CSS for Enter OTP */
              .enterotp-verify-input::placeholder {
                letter-spacing: normal !important;
                font-size: 20px !important;
                text-align: center !important;
                text-align-last: center !important;
                color: #8A8A8A !important;
                opacity: 1 !important;
                font-family: 'Inter', Inter, sans-serif !important;
                line-height: 1 !important;
                vertical-align: middle !important;
                padding: 0 !important;
                padding-bottom: 16px !important;
                margin: 0 !important;
              }
              
              .enterotp-verify-input:focus { 
                background: #fff; 
                border: 0.66px solid #8A8A8A; 
                box-shadow: none !important; 
                outline: none !important; 
              }
              
              .otp-verify-input:focus { background: #fff; border: 0.66px solid #8A8A8A; box-shadow: none !important; outline: none !important; }
              .email-input { font-size: 16px; text-align: left; letter-spacing: normal; padding: 0 12px; }
              .email-input::placeholder { 
                font-size: 14px !important; 
                letter-spacing: normal !important; 
                color: #8A8A8A !important;
                opacity: 1 !important;
                font-family: 'Inter', Inter, sans-serif !important;
              }
              .otp-verify-btn {
              width: 100%;
      background: #ffff;
      color: #fff;
      border: none;
      border-radius: 8px;
      height: 48px;
      font-size: 18px;
      margin-bottom: 10px;
      cursor: pointer;
      box-shadow: #ffff;
      transition: background 0.2s;
               }
              .otp-verify-btn:hover { background: #303030; }
              .otp-verify-bottom-row { display: flex; align-items: left; gap: 8px;  }
              .otp-verify-bottom-label { font-family: Inter, sans-serif; padding-top: 13px; font-size: 13px; font-weight: 650; color: #000; letter-spacing: 0px; line-height: 20px; }
              .otp-verify-change-label {
                text-align: left;
                color: #222;
                margin-bottom: 12px;
                font-size: 15px;
                font-family: 'Inter', Inter, sans-serif;
                font-weight: 650;
                letter-spacing: 0;
                line-height: 20px;
                padding: 6px 0;
              }
              .otp-verify-bottom-link { font-family: Inter, sans-serif; padding-top: 10px; font-size: 14px; font-weight: 650; color: #00527C; text-decoration: underline; cursor: pointer; }
              .otp-divider { display: flex; align-items: center; gap: 16px; width: 100%; margin: 5px 2px 7px 0; }
              .otp-divider-hr { flex: 1; border: none; border-top: 1px solid #8A8A8A; height: 1px; background: none; margin: 0; }
              .otp-divider-text { color: #000; font-family: 'SF Pro Text', Inter, sans-serif; font-size: 12px; font-weight: 600; }
              .otp-social-row { display: flex; gap: 8px; width: 100%; margin: -4px 0 8px 0; }
              .otp-social-btn { flex: 1; display: flex; align-items: center; justify-content: center; background: #fff; border: 1px solid #E3E3E3; border-radius: 8px; height: 52px; cursor: pointer; transition: border 0.2s; }
              .otp-social-btn:focus { outline: none; border: 1.5px solid #8A8A8A; }
              .otp-social-btn img { width: 28px; height: 28px; display: block; }
              .otp-social-btn span { margin-left: 8px; font-size: 16px; color: #303030; font-family: Inter, sans-serif; }
              .otp-email-btn {
                        width: 100%;       
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 8px;
        line-height: 45px; 
        font-size: 18px;
        font-family: Inter, sans-serif;
        font-weight:500;
        cursor: pointer;
        color: #303030;
        transition: background 0.2s, border 0.2s; 
                }
              .otp-email-btn:focus { outline: none; border: 1.5px solid #8A8A8A; }
              .otp-spinner {
                display: inline-block;
                width: 22px;
                height: 22px;
                border: 3px solid #fff;
                border-radius: 50%;
                border-top: 3px solid #1A1A1A;
                animation: otp-spin 0.7s linear infinite;
                vertical-align: middle;
                margin-right: 8px;
              }
              @keyframes otp-spin {
                0% { transform: rotate(0deg);}
                100% { transform: rotate(360deg);}
              }
            </style>
            <h2 class="otp-verify-title">${translate('enterOTP')}</h2>
            <div id="otp-message" style="margin-bottom: 1rem; color: red;"></div>
            <div class="otp-verify-desc"><b>${translate('otpSentSuccess')}</b></div>
            
             <div class="otp-verify-bottom-row">
                <p class="otp-verify-change-label">${getCountryDisplayName(identifier)} ${formatPhoneDisplay(identifier)}  <a href="#" id="otp-change-link" class="otp-verify-bottom-link">${translate('change')}</a></p>
               
            </div>
            <form id="otp-form">
              <input type="hidden" name="step" value="verify" />
              <input type="hidden" name="identifier" value="${identifier}" />
              <div id="otp-input-wrapper" class="otp-verify-form-row">
                <input name="otp" placeholder="${translate('enterOTPPlaceholder')}" class="enterotp-verify-input" />
              </div>
              <div id="name-input-wrapper" class="otp-verify-form-row" style="display:none;">
                <input name="first_name" placeholder="${translate('firstName')}" class="otp-verify-input" style="flex:1;" />
                <input name="last_name" placeholder="${translate('lastName')}" class="otp-verify-input" style="flex:1;" />
              </div>
              <div id="email-input-wrapper" class="otp-verify-form-row" style="display:none;">
                <input name="email" placeholder="${translate('emailPlaceholder')}" class="otp-verify-input email-input" style="flex:1;" />
              </div>
              <button type="submit" class="otp-verify-btn">${translate('submitOTP')}</button>
              <div class="otp-verify-bottom-row">
                <span class="otp-verify-bottom-label">${translate('otpNotReceived')}</span>
                <a href="#" id="otp-resend-link" class="otp-verify-bottom-link">${translate('resendOTP')}</a>
              </div>
            </div>
          </form>
            ${showSocialDivider ? `
              <div class="otp-divider">
                <hr class="otp-divider-hr" />
                <span class="otp-divider-text">${translate('or')}</span>
                <hr class="otp-divider-hr" />
              </div>
              ${generateSocialButtonsHTML()}
            ` : ''}
            ${showEmailPassword2 ? `<button type="button" class="otp-email-btn">${translate('loginEmailPassword')}</button>` : ''}
            ${!shouldHidePoweredBy() ? `<div style="font-size: 10px; text-align: right; padding-top: 8px;">${translate('poweredBy')}</div>` : ''}
        `;
        // Apply dynamic button colors
        applyDynamicButtonColors();
        // Apply placeholder protection
        applyPlaceholderProtection(otpContainer);
        // Re-attach main handler to new OTP form
        const newForm = otpContainer.querySelector('#otp-form');
        attachOtpFormHandler(newForm, otpContainer);
        // Add OTP input restrictions: only 4 digits, numeric only
        if (newForm.querySelector('input[name="otp"]')) {
          const otpInput = newForm.querySelector('input[name="otp"]');
          otpInput.setAttribute('maxlength', '4');
          otpInput.setAttribute('inputmode', 'numeric');
          otpInput.setAttribute('pattern', '\\d{4}');
          otpInput.addEventListener('input', function (e) {
            // Remove non-numeric characters
            let val = otpInput.value.replace(/[^0-9]/g, '');
            // Limit to 4 digits
            if (val.length > 4) val = val.slice(0, 4);
            otpInput.value = val;
          });
        }

        // Attach click handler to email button if it exists
        const emailButton = otpContainer.querySelector('.otp-email-btn');
        if (emailButton) {
          emailButton.addEventListener('click', function (e) {
            e.preventDefault();
            console.log('📧 Email login button clicked (verify screen) - showing Shopify default login');
            try {
              showShopifyDefaultLoginForm();
            } catch (error) {
              console.error('❌ Error calling showShopifyDefaultLoginForm:', error);
            }
          });
        }

        // Change and Resend OTP links
        otpContainer.querySelector('#otp-change-link').addEventListener('click', (e) => {
          e.preventDefault();
          // Get the identifier (phone) from the hidden input
          const identifierValue = newForm.querySelector('input[name="identifier"]').value;
          // Extract phone number without country code and get the country code
          const phoneWithoutCountryCode = extractPhoneWithoutCountryCode(identifierValue);
          const countryCode = getCountryCodeFromPhone(identifierValue);

          // Inject RTL styles if Arabic
          injectRTLStyles();

          // Re-render the Get OTP form with the phone pre-filled and loading state
          renderInitialForm(otpContainer, { loadingSocial: true, prefillPhone: phoneWithoutCountryCode, selectedCountryCode: countryCode });
          applyPlaceholderProtection(otpContainer);
          fetchHyperOTPSettings().then(() => {
            renderInitialForm(otpContainer, { loadingSocial: false, prefillPhone: phoneWithoutCountryCode, selectedCountryCode: countryCode });
            applyPlaceholderProtection(otpContainer);
            const { google, facebook } = getEnabledSocialLogins();
            const showOTPForm = shouldShowOTPForm();
            const showEmailPassword = shouldShowEmailPasswordLogin();
            const googleButton = otpContainer.querySelector('.google-sso-btn');
            const facebookButton = otpContainer.querySelector('button:has(img[alt="Facebook"])');
            const emailButton = otpContainer.querySelector('.email-btn');
            const divider = otpContainer.querySelector('.divider');
            const otpFormElement = otpContainer.querySelector('#otp-form');
            if (googleButton) {
              if (google) {
                googleButton.disabled = false;
                googleButton.style.opacity = '1';
              } else {
                googleButton.style.display = 'none';
              }
            }
            if (facebookButton) {
              if (facebook) {
                facebookButton.disabled = false;
                facebookButton.style.opacity = '1';
              } else {
                facebookButton.style.display = 'none';
              }
            }
            if (emailButton) {
              if (showEmailPassword) {
                emailButton.disabled = false;
                emailButton.style.opacity = '1';
              } else {
                emailButton.style.display = 'none';
              }
            }
            if (otpFormElement) {
              otpFormElement.style.display = showOTPForm ? 'block' : 'none';
            }
            if (divider && !google && !facebook && !showEmailPassword) {
              divider.style.display = 'none';
            }
            attachGoogleSSOHandlers();
            attachFacebookSSOHandlers();
          });
        });
        otpContainer.querySelector('#otp-resend-link').addEventListener('click', async (e) => {
          e.preventDefault();
          const resendLink = otpContainer.querySelector('#otp-resend-link');
          if (resendLink) {
            resendLink.innerHTML = '<span class="otp-spinner" style="display:inline-block;vertical-align:middle;min-width:22px;min-height:22px;"></span>';
            resendLink.style.pointerEvents = 'none';
          }
          // Get the identifier (phone) from the hidden input
          const identifierValue = newForm.querySelector('input[name="identifier"]').value;
          // Directly call the backend to resend OTP
          const messageBox = otpContainer.querySelector('#otp-message');
          try {
            const res = await fetch(getAppProxyUrl(), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: identifierValue, step: 'send' })
            });
            const result = await res.json();
            if (result.success) {
              if (messageBox) {
                messageBox.style.color = 'green';
                messageBox.innerText = result.message || translate('otpSent');
              }
            } else {
              if (messageBox) {
                messageBox.style.color = 'red';
                messageBox.innerText = result.error || translate('failedSendOTP');
              }
            }
          } catch (err) {
            if (messageBox) {
              messageBox.style.color = 'red';
              messageBox.innerText = translate('failedSendOTP');
            }
          } finally {
            if (resendLink) {
              resendLink.innerHTML = translate('resendOTP');
              resendLink.style.pointerEvents = '';
            }
          }
        });
        return;
      } else {
        if (messageBox) {
          messageBox.innerText = result.error || translate('failedSendOTP');
          messageBox.style.color = "red";
        }
        submitButton.innerHTML = translate('sendOTP');
      }
    } else if (step === "verify" || step === "complete") {
      // Validate OTP input
      if (!otp || otp.length !== 4 || !/^[0-9]{4}$/.test(otp)) {
        if (messageBox) {
          messageBox.style.color = "red";
          messageBox.innerText = translate('validOTP');
        }
        return;
      }
      // If step is complete, validate email
      if (step === "complete") {
        if (!emailInput || !emailInput.value.trim()) {
          if (messageBox) {
            messageBox.style.color = "red";
            messageBox.innerText = translate('enterEmail');
          }
          return;
        }
      }
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      // Prepare payload
      let payload = { phone: identifier, otp, step };
      if (step === "complete") payload.email = emailInput.value.trim();
      const res = await fetch(getAppProxyUrl(), {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      // console.log("[OTP VERIFY/COMPLETE RESPONSE]", result);
      submitButton.disabled = false;

      // Handle requireEmail response from backend
      if (result.requireEmail) {
        // Hide OTP input, show email input
        if (emailInputWrapper) emailInputWrapper.style.display = '';
        if (otpInputWrapper) otpInputWrapper.style.display = 'none';
        if (submitButton) submitButton.innerText = translate('continueBtn');
        if (stepInput) stepInput.value = 'update_email';
        form.dataset.customerId = result.customerId;
        if (messageBox) {
          messageBox.style.color = 'red';
          messageBox.innerText = result.message || 'Please enter your email to continue.';
        }
        return;
      }

      // NEW: If new customer, show signup form (name/email)
      if (result.success && result.existingCustomer === false) {
        // Render a separate signup form for new customers
        // Inject RTL styles if Arabic
        injectRTLStyles();
        otpContainer.style.background = '#fff';
        otpContainer.style.borderRadius = '16px';
        otpContainer.style.border = '1px solid #E3E3E3';
        otpContainer.style.boxShadow = '0px 3px 1px -1px rgba(26,26,26,0.07), inset 0px 1px 0px 0px rgba(204,204,204,0.5), inset 0px -1px 0px 0px rgba(0,0,0,0.17), inset -1px 0px 0px 0px rgba(0,0,0,0.13), inset 1px 0px 0px 0px rgba(0,0,0,0.13)';
        otpContainer.style.maxWidth = '484px';
        otpContainer.style.margin = '40px auto';
        otpContainer.style.padding = '40px 32px 32px 32px';
        otpContainer.style.display = 'flex';
        otpContainer.style.flexDirection = 'column';
        otpContainer.style.gap = '0';
        // Check registerFields setting
        const registerFields = (window.hyperOTPSettings && window.hyperOTPSettings.registerFields) ? window.hyperOTPSettings.registerFields : '';
        // Check acceptEnabled setting
        const acceptEnabled = (window.hyperOTPSettings && window.hyperOTPSettings.acceptEnabled) ? window.hyperOTPSettings.acceptEnabled : false;
        // Use safe translation reference in template string - use window.hyperOTPTranslate directly to avoid shadowing
        const translateSignup = window.hyperOTPTranslate || translationFunction || getTranslation;
        otpContainer.innerHTML = `
          <style>
            #signup-form {
              width: 100%;
              display: flex;
              flex-direction: column;
              gap: 0;
            }
            .signup-title {
              font-family: Inter, sans-serif;
              font-size: 30px;
              font-weight: 700;
              color: #000;
              margin-bottom: 0.5rem;
              text-align: left;
              letter-spacing: -0.3px;
              line-height: 40px;
            }
            .signup-desc {
              color: #616161;
              font-size: 14px;
              font-family: Inter, sans-serif;
              font-weight: 650;
              margin-bottom: 1.5rem;
              text-align: left;
              line-height: 1.43;
            }
            .signup-form-row {
              display: flex;
              flex-direction: column;
              gap: 4px;
              margin-bottom: 1.2rem;
              align-items: flex-start;
              width: 100%;
            }
            .signup-label {
              font-family: Inter, sans-serif;
              font-size: 13px;
              font-weight: 450;
              color: #303030;
              margin-bottom: 2px;
              text-align: left;
              width: 100%;
            }
            .signup-input {
              width: 100%;
              height: 45px;
              padding: 0 16px;
              border: 1px solid #8A8A8A;
              background: #FDFDFD;
              font-size: 18px;
              color: #303030;
              font-family: Inter, sans-serif;
              border-radius: 8px;
              outline: none;
              transition: border 0.2s;
              box-shadow: none !important;
              box-sizing: border-box;
            }
            .signup-input:focus {
              border: 1px solid #8A8A8A;
              background: #fff;
              box-shadow: none !important;
              outline: none !important;
            }
            .signup-input[readonly] {
              background: #F5F5F5;
              color: #8A8A8A;
            }
            .signup-input::placeholder {
              color: #8A8A8A !important;
              font-size: 18px !important;
              font-family: 'Inter', Inter, sans-serif !important;
              opacity: 1 !important;
              letter-spacing: normal !important;
              text-align: inherit !important;
            }
            .signup-btn {
              background: #1A1A1A;
              color: #fff;
              border: none;
              font-size: 18px;
              font-family: Inter, sans-serif;
              font-weight: 550;
              height: 52px;
              width: 100%;
              margin-top: 8px;
              cursor: pointer;
              border-radius: 8px;
              transition: background 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0px 3px 1px -1px rgba(26,26,26,0.07);
              position: relative;
              overflow: hidden;
            }
            .signup-btn::after {
              content: '';
              position: absolute;
              left: 0; top: 0; right: 0; bottom: 0;
              background: linear-gradient(90deg,rgba(255,255,255,0.07) 88%,rgba(255,255,255,0.15) 100%);
              pointer-events: none;
              border-radius: 8px;
            }
            .signup-btn:hover {
              background: #303030;
            }
            .signup-checkbox-wrapper {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              cursor: pointer;
              margin-bottom: 8px;
            }
            .signup-checkbox {
              width: 18px;
              height: 18px;
              margin: 2px 0 0 0;
              accent-color: #1A1A1A;
              cursor: pointer;
            }
            .signup-checkbox-label {
              font-family: Inter, sans-serif;
              font-size: 14px;
              font-weight: 400;
              color: #303030;
              line-height: 1.43;
              text-align: left;
              cursor: pointer;
            }
            .otp-spinner {
              display: inline-block;
              width: 22px;
              height: 22px;
              border: 3px solid #fff;
              border-radius: 50%;
              border-top: 3px solid #1A1A1A;
              animation: otp-spin 0.7s linear infinite;
              vertical-align: middle;
              margin-right: 8px;
            }
            @keyframes otp-spin {
              0% { transform: rotate(0deg);}
              100% { transform: rotate(360deg);}
            }
          </style>
          <div class="signup-title">${translateSignup('contactInformation')}</div>
          <div class="signup-desc">${translateSignup('contactDescription')}</div>
          <form id="signup-form" autocomplete="off">
            ${registerFields === 'email' ? '' : `
            <div class="signup-form-row">
              <label class="signup-label" for="first_name">${translateSignup('firstName')}</label>
              <input name="first_name" id="first_name" placeholder="${translateSignup('firstNamePlaceholder')}" class="signup-input" />
            </div>
            <div class="signup-form-row">
              <label class="signup-label" for="last_name">${translateSignup('lastName')}</label>
              <input name="last_name" id="last_name" placeholder="${translateSignup('lastNamePlaceholder')}" class="signup-input" />
            </div>
            `}
            <div class="signup-form-row">
              <label class="signup-label" for="email">${translateSignup('emailAddress')} *</label>
              <input name="email" id="email" placeholder="${translateSignup('emailPlaceholder')}" class="signup-input" type="email" required />
            </div>
            ${acceptEnabled ? `
            <div class="signup-form-row">
              <label class="signup-checkbox-wrapper">
                <input type="checkbox" name="subscription" id="subscription" class="signup-checkbox" />
                <span class="signup-checkbox-label">${translateSignup('subscribeNewsletter')}</span>
              </label>
            </div>
            ` : ''}
            <input type="hidden" name="phone" id="phone" value="${identifier}" />
            <button type="submit" class="signup-btn">${translateSignup('login')}</button>
          </form>
        `;
        // Apply dynamic button colors
        applyDynamicButtonColors();
        // Apply placeholder protection
        applyPlaceholderProtection(otpContainer);
        // Attach handler for the new signup form
        const signupForm = otpContainer.querySelector('#signup-form');
        signupForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          const firstName = signupForm.querySelector('input[name="first_name"]')?.value.trim() || '';
          const lastName = signupForm.querySelector('input[name="last_name"]')?.value.trim() || '';
          const email = signupForm.querySelector('input[name="email"]')?.value.trim() || '';
          const phone = signupForm.querySelector('input[name="phone"]').value.trim();
          const subscription = signupForm.querySelector('input[name="subscription"]')?.checked || false;
          const submitBtn = signupForm.querySelector('button[type="submit"]');
          // Only require email
          if (!email) {
            const translateSignup = window.hyperOTPTranslate || translationFunction || getTranslation;
            alert(translateSignup('enterEmail'));
            return;
          }
          submitBtn.disabled = true;
          submitBtn.innerHTML = '<span class="otp-spinner"></span>';
          const res = await fetch(getAppProxyUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              phone,
              email,
              first_name: registerFields !== 'email' ? firstName : '',
              last_name: registerFields !== 'email' ? lastName : '',
              subscription,
              step: 'complete'
            })
          });
          const result = await res.json();
          submitBtn.disabled = false;
          if (result.success && result.email && result.password) {
            // Submit login form automatically with CAPTCHA
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/account/login';
            const emailInput = document.createElement('input');
            emailInput.type = 'hidden';
            emailInput.name = 'customer[email]';
            emailInput.value = result.email;
            form.appendChild(emailInput);
            const passwordInput = document.createElement('input');
            passwordInput.type = 'hidden';
            passwordInput.name = 'customer[password]';
            passwordInput.value = result.password;
            form.appendChild(passwordInput);
            const formTypeInput = document.createElement('input');
            formTypeInput.type = 'hidden';
            formTypeInput.name = 'form_type';
            formTypeInput.value = 'customer_login';
            form.appendChild(formTypeInput);
            const urlParams = new URLSearchParams(window.location.search);
            const returnUrl = result.return_url || urlParams.get('return_url') || urlParams.get('return_to');
            const checkoutUrl = urlParams.get('checkout_url');

            if (returnUrl) {
              const returnInput = document.createElement('input');
              returnInput.type = 'hidden';
              returnInput.name = 'return_to';
              returnInput.value = returnUrl;
              form.appendChild(returnInput);
            }
            if (checkoutUrl) {
              const checkoutInput = document.createElement('input');
              checkoutInput.type = 'hidden';
              checkoutInput.name = 'checkout_url';
              checkoutInput.value = checkoutUrl;
              form.appendChild(checkoutInput);
            }
            document.body.appendChild(form);
            if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
              window.Shopify.captcha.protect(form, () => { form.submit(); });
            } else {
              form.submit();
            }
            return;
          }
          if (result.error) {
            alert(result.error);
          } else {
            alert(result.message || 'Signup complete!');
          }
        });
        return;
      }
      // Success: login as usual
      if (result.success && result.email && result.password) {
        // Submit login form automatically with CAPTCHA
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/account/login';
        const emailInputHidden = document.createElement('input');
        emailInputHidden.type = 'hidden';
        emailInputHidden.name = 'customer[email]';
        emailInputHidden.value = result.email;
        form.appendChild(emailInputHidden);
        const passwordInput = document.createElement('input');
        passwordInput.type = 'hidden';
        passwordInput.name = 'customer[password]';
        passwordInput.value = result.password;
        form.appendChild(passwordInput);
        const formTypeInput = document.createElement('input');
        formTypeInput.type = 'hidden';
        formTypeInput.name = 'form_type';
        formTypeInput.value = 'customer_login';
        form.appendChild(formTypeInput);
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = result.return_url || urlParams.get('return_url') || urlParams.get('return_to');
        const checkoutUrl = urlParams.get('checkout_url');

        if (returnUrl) {
          const returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_to';
          returnInput.value = returnUrl;
          form.appendChild(returnInput);
        }
        if (checkoutUrl) {
          const checkoutInput = document.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = 'checkout_url';
          checkoutInput.value = checkoutUrl;
          form.appendChild(checkoutInput);
        }
        document.body.appendChild(form);
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(form, () => { form.submit(); });
        } else {
          form.submit();
        }
        return;
      }
      if (result.error) {
        if (messageBox) {
          messageBox.style.color = 'red';
          messageBox.innerText = result.error;
        }
      } else if (result.message) {
        if (messageBox) {
          messageBox.style.color = 'green';
          messageBox.innerText = result.message;
        }
      }
      submitButton.innerHTML = step === 'complete' ? translate('continueBtn') : translate('submitOTP');
      return;
    } else if (step === "update_email") {
      // On submit, if step is update_email, send correct payload
      if (!emailInput || !emailInput.value.trim()) {
        if (messageBox) {
          messageBox.style.color = "red";
          messageBox.innerText = translate('enterEmail');
        }
        return;
      }
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="otp-spinner"></span>';
      let payload = {
        step: "update_email",
        customerId: form.dataset.customerId,
        email: emailInput.value.trim(),
        phone: identifier
      };
      const res2 = await fetch(getAppProxyUrl(), {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result2 = await res2.json();
      submitButton.disabled = false;
      // Handle login as normal
      if (result2.success && result2.email && result2.password) {
        // Submit login form automatically with CAPTCHA
        const formEl = document.createElement('form');
        formEl.method = 'POST';
        formEl.action = '/account/login';
        const emailInputHidden = document.createElement('input');
        emailInputHidden.type = 'hidden';
        emailInputHidden.name = 'customer[email]';
        emailInputHidden.value = result2.email;
        formEl.appendChild(emailInputHidden);
        const passwordInput = document.createElement('input');
        passwordInput.type = 'hidden';
        passwordInput.name = 'customer[password]';
        passwordInput.value = result2.password;
        formEl.appendChild(passwordInput);
        const formTypeInput = document.createElement('input');
        formTypeInput.type = 'hidden';
        formTypeInput.name = 'form_type';
        formTypeInput.value = 'customer_login';
        formEl.appendChild(formTypeInput);
        const urlParams = new URLSearchParams(window.location.search);
        const returnUrl = result2.return_url || urlParams.get('return_url') || urlParams.get('return_to');
        const checkoutUrl = urlParams.get('checkout_url');

        if (returnUrl) {
          const returnInput = document.createElement('input');
          returnInput.type = 'hidden';
          returnInput.name = 'return_to';
          returnInput.value = returnUrl;
          formEl.appendChild(returnInput);
        }
        if (checkoutUrl) {
          const checkoutInput = document.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = 'checkout_url';
          checkoutInput.value = checkoutUrl;
          formEl.appendChild(checkoutInput);
        }
        document.body.appendChild(formEl);
        if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
          window.Shopify.captcha.protect(formEl, () => { formEl.submit(); });
        } else {
          formEl.submit();
        }
        return;
      }
      if (result2.error) {
        if (messageBox) {
          messageBox.style.color = 'red';
          messageBox.innerText = result2.error;
        }
      } else if (result2.message) {
        if (messageBox) {
          messageBox.style.color = 'green';
          messageBox.innerText = result2.message;
        }
      }
      submitButton.innerHTML = translate('continueBtn');
      return;
    }
  });
}




/**
 * SECTION: SSO Management
 * Handles Single Sign-On (SSO) functionality for Google and Facebook
 */

/**
 * Manages SSO popup state to prevent multiple popups
 */
function isSSOPopupOpened() {
  return window._ssoPopupOpened || sessionStorage.getItem('ssoPopupOpened') === 'true';
}

function setSSOPopupOpened(val) {
  window._ssoPopupOpened = val;
  sessionStorage.setItem('ssoPopupOpened', val ? 'true' : 'false');
}

// Reset SSO popup state on page load
setSSOPopupOpened(false);

/**
 * SECTION: Google SSO Integration
 * Handles Google Single Sign-On functionality
 */

/**
 * Opens the Google SSO popup window
 */
function openGoogleSSOPopup() {
  console.log("openGoogleSSOPopup called");
  if (isSSOPopupOpened()) {
    console.log("SSO popup already opened, skipping");
    return;
  }
  setSSOPopupOpened(true);
  var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com';
  var urlParams = new URLSearchParams(window.location.search);
  var returnUrl = urlParams.get('return_url');
  var popupUrl = 'https://otp.zoketo.com/app/authgoogle?shop=' + encodeURIComponent(shop);
  if (returnUrl) {
    popupUrl += '&return_url=' + encodeURIComponent(returnUrl);
  }
  console.log("Opening SSO popup for shop:", shop, "with return_url:", returnUrl);
  var popup = window.open(popupUrl, 'googleSSO', 'width=500,height=600');

  // Monitor popup close event
  var checkClosed = setInterval(function () {
    if (popup.closed) {
      console.log("Google SSO popup was closed");
      setSSOPopupOpened(false);
      clearInterval(checkClosed);
    }
  }, 1000);
}

/**
 * Attaches click handlers to Google SSO buttons
 * Handles both static and dynamically added buttons
 */
function attachGoogleSSOHandlers() {
  document.querySelectorAll('.google-sso-btn').forEach(btn => {
    btn.removeEventListener('click', btn._googleSSOHandler);
    btn._googleSSOHandler = function (event) {
      console.log("Google SSO button clicked");
      event.preventDefault();
      openGoogleSSOPopup();
      return false;
    };
    btn.addEventListener('click', btn._googleSSOHandler);
  });
}

// Initial attach for Google SSO
attachGoogleSSOHandlers();

// Watch for dynamic UI changes
const googleSSOObserver = new MutationObserver(() => {
  attachGoogleSSOHandlers();
});
googleSSOObserver.observe(document.body, { childList: true, subtree: true });

/**
 * SECTION: Facebook SSO Integration
 * Handles Facebook Single Sign-On functionality
 */

/**
 * Opens the Facebook SSO popup window
 */
function openFacebookSSOPopup() {
  console.log("openFacebookSSOPopup called");
  if (isSSOPopupOpened()) {
    console.log("SSO popup already opened, skipping");
    return;
  }
  setSSOPopupOpened(true);
  var shop = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : 'yourshop.myshopify.com';
  console.log("Opening Facebook SSO popup for shop:", shop);
  var popup = window.open('https://otp.zoketo.com/app/authfacebook?shop=' + encodeURIComponent(shop), 'facebookSSO', 'width=500,height=600');

  // Monitor popup close event
  var checkClosed = setInterval(function () {
    if (popup.closed) {
      console.log("Facebook SSO popup was closed");
      setSSOPopupOpened(false);
      clearInterval(checkClosed);
    }
  }, 1000);
}

/**
 * Attaches click handlers to Facebook SSO buttons
 * Handles both static and dynamically added buttons
 */
function attachFacebookSSOHandlers() {
  // Handle buttons with facebook-sso-btn class
  document.querySelectorAll('.facebook-sso-btn').forEach(btn => {
    // Remove any old onclick attributes
    btn.removeAttribute('onclick');
    btn.removeEventListener('click', btn._facebookSSOHandler);
    btn._facebookSSOHandler = function (event) {
      console.log("Facebook SSO button clicked");
      event.preventDefault();
      openFacebookSSOPopup();
      return false;
    };
    btn.addEventListener('click', btn._facebookSSOHandler);
  });

  // Also handle generic social buttons with Facebook images (fallback)
  document.querySelectorAll('.social-btn').forEach(btn => {
    if (btn.querySelector('img[alt="Facebook"]')) {
      // Remove any old onclick attributes
      btn.removeAttribute('onclick');
      btn.removeEventListener('click', btn._facebookSSOHandler);
      btn._facebookSSOHandler = function (event) {
        console.log("Facebook SSO button clicked");
        event.preventDefault();
        openFacebookSSOPopup();
        return false;
      };
      btn.addEventListener('click', btn._facebookSSOHandler);
    }
  });
}

// Initial attach for Facebook SSO
attachFacebookSSOHandlers();

// Watch for dynamic UI changes
const fbObserver = new MutationObserver(() => {
  attachFacebookSSOHandlers();
});
fbObserver.observe(document.body, { childList: true, subtree: true });

/**
 * SECTION: SSO Message Handler
 * Processes messages from SSO popups and handles login
 */
window.addEventListener("message", function (event) {
  if (event.data && event.data.type === "shopify-sso-login") {
    setSSOPopupOpened(false); // Reset flag after successful login

    // Create and submit login form
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/account/login';

    // Add login credentials
    const emailInput = document.createElement('input');
    emailInput.type = 'hidden';
    emailInput.name = 'customer[email]';
    emailInput.value = event.data.email;
    form.appendChild(emailInput);

    const passwordInput = document.createElement('input');
    passwordInput.type = 'hidden';
    passwordInput.name = 'customer[password]';
    passwordInput.value = event.data.password;
    form.appendChild(passwordInput);

    // Add form type for Shopify CAPTCHA
    const formTypeInput = document.createElement('input');
    formTypeInput.type = 'hidden';
    formTypeInput.name = 'form_type';
    formTypeInput.value = 'customer_login';
    form.appendChild(formTypeInput);

    // Use return_url from backend if present, else from query param
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = event.data.return_url || urlParams.get('return_url') || urlParams.get('return_to');
    const checkoutUrl = urlParams.get('checkout_url');

    console.log("[SSO] Submitting login form with return_url:", returnUrl, "checkout_url:", checkoutUrl);

    if (returnUrl) {
      const returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      returnInput.value = returnUrl;
      form.appendChild(returnInput);
    }
    if (checkoutUrl) {
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout_url';
      checkoutInput.value = checkoutUrl;
      form.appendChild(checkoutInput);
    }

    // Submit form with CAPTCHA protection if available
    document.body.appendChild(form);
    if (window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
      window.Shopify.captcha.protect(form, () => { form.submit(); });
    } else {
      form.submit();
    }
  }
});

/**
 * Renders the initial login/signup form with optional loading state for social buttons
 * @param {HTMLElement} otpContainer - The container to render into
 * @param {Object} options - { loadingSocial: boolean, prefillPhone: string }
 */
function renderInitialForm(otpContainer, options = {}) {
  const { loadingSocial = false, prefillPhone = '', selectedCountryCode = '' } = options;

  // Inject RTL styles if Arabic
  injectRTLStyles();

  // Use safe translation reference in template string - use window.hyperOTPTranslate directly to avoid shadowing
  const translateInit = window.hyperOTPTranslate || translationFunction || getTranslation;

  otpContainer.innerHTML = `
    <style>
      #otp-login-container h2 { margin-top: 10px; margin-bottom: 10px; font-size: 30px; font-weight: 700; letter-spacing: -0.3px; }
      .input-group {
        display: flex;
        align-items: center;
        border: 1px solid #ccc;
        border-radius: 8px;
        padding: 0 12px;
        background: #fff;
        margin-bottom: 8px;
        height: 48px;
      }
      .input-group select, .input-group input {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        outline: none !important;
        font-size: 16px;
        font-family: Inter, sans-serif;
        font-weight: 500;
        color: #222;
        margin: 0;
        padding: 0;
      }
      .input-group input::placeholder,
      .input-group input[name="identifier"]::placeholder {
        color: #8A8A8A !important;
        opacity: 1 !important;
        font-size: 16px !important;
        font-family: 'Inter', Inter, sans-serif !important;
        letter-spacing: normal !important;
      }
      .otp-btn {
        width: 100%;
        background: #ffff;
        color: #fff;
        border: none;
        border-radius: 8px;
        height: 48px;
        font-size: 18px;
        margin-bottom: 10px;
        cursor: pointer;
        box-shadow: #ffff;
        transition: background 0.2s;
      }
      .otp-btn:hover { background: linear-gradient(180deg, #333 0%, #555 100%); }
      .divider { display: flex; align-items: center; text-align: center; margin-bottom: 12px; margin-top: 6px; }
      .divider::before, .divider::after { content: ''; flex: 1; border-bottom: 1.5px solid #ddd; }
      .divider:not(:empty)::before { margin-right: .75em; }
      .divider:not(:empty)::after { margin-left: .75em; }
      .social-row {
        display: flex;
        gap: 16px;
        margin-bottom: 8px;
      }
      .social-btn {
        flex: 1;
        display: flex !important;
        align-items: center;
        justify-content: center;
        background: #fff !important;
        border: 1px solid #ddd !important;
        border-radius: 8px !important;
        height: 48px !important;
        width: 100% !important;
        cursor: pointer;
        font-size: 1.1rem;
        transition: background 0.2s, border 0.2s;
        padding: 0 !important;
        margin: 0 !important;
        min-width: auto !important;
        max-width: none !important;
        box-sizing: border-box !important;
      }
      .social-btn:disabled {
        cursor: not-allowed;
      }
      .social-btn:hover {
        background: #f0f0f0;
        border-color: #bbb;
      }
      .social-btn img {
        width: 24px;
        height: 24px;
        display: block;
      }
      
      /* Mobile-specific overrides to prevent Shopify button CSS interference */
      @media (max-width: 768px) {
        .social-btn {
          display: flex !important;
          flex: 1 !important;
          width: 100% !important;
          min-width: 0 !important;
          max-width: none !important;
          padding: 0 !important;
          margin: 0 !important;
          background: #fff !important;
          border: 1px solid #ddd !important;
          border-radius: 8px !important;
          height: 48px !important;
          box-sizing: border-box !important;
          position: relative !important;
          overflow: visible !important;
        }
        
        .social-row {
          display: flex !important;
          gap: 16px !important;
          width: 100% !important;
          margin-bottom: 8px !important;
        }
        
        .social-btn img {
          width: 24px !important;
          height: 24px !important;
          display: block !important;
        }
      }
      .email-btn {
        width: 100% !important;       
        background: #fff !important;
        border: 1px solid #ddd !important;
        border-radius: 8px !important;
        line-height: 45px !important; 
        font-size: 18px !important;
        font-family: Inter, sans-serif !important;
        font-weight: 500 !important;
        cursor: pointer !important;
        color: #303030 !important;
        transition: background 0.2s, border 0.2s !important;
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: none !important;
        outline: none !important;
        text-decoration: none !important;
        text-transform: none !important;
        letter-spacing: normal !important;
      }
      .email-btn:hover { 
        background: #f0f0f0 !important; 
        border-color: #bbb !important; 
      }
      .email-btn:focus { 
        outline: none !important; 
        box-shadow: none !important; 
      }
      .email-btn:active { 
        transform: none !important; 
      }
      /* More specific selector to override theme styles */
      button.email-btn,
      button[type="button"].email-btn,
      .otp-container button.email-btn {
        width: 100% !important;       
        background: #fff !important;
        border: 1px solid #ddd !important;
        border-radius: 8px !important;
        line-height: 45px !important; 
        font-size: 18px !important;
        font-family: Inter, sans-serif !important;
        font-weight: 500 !important;
        cursor: pointer !important;
        color: #303030 !important;
        transition: background 0.2s, border 0.2s !important;
        margin: 0 !important;
        padding: 0 !important;
        box-shadow: none !important;
        outline: none !important;
        text-decoration: none !important;
        text-transform: none !important;
        letter-spacing: normal !important;
      }
      button.email-btn:hover,
      button[type="button"].email-btn:hover,
      .otp-container button.email-btn:hover { 
        background: #f0f0f0 !important; 
        border-color: #bbb !important; 
      }
      button.email-btn:focus,
      button[type="button"].email-btn:focus,
      .otp-container button.email-btn:focus { 
        outline: none !important; 
        box-shadow: none !important; 
      }
      button.email-btn:active,
      button[type="button"].email-btn:active,
      .otp-container button.email-btn:active { 
        transform: none !important; 
      }
      .otp-spinner {
        display: inline-block;
        width: 22px;
        height: 22px;
        border: 3px solid #fff;
        border-radius: 50%;
        border-top: 3px solid #1A1A1A;
        animation: otp-spin 0.7s linear infinite;
        vertical-align: middle;
        margin-right: 8px;
      }
      @keyframes otp-spin {
        0% { transform: rotate(0deg);}
        100% { transform: rotate(360deg);}
      }
    </style>
    <h2 style="color: ${window.hyperOTPSettings?.loginHeaderColor || 'white'}; font-size: 22px;">${isArabic() ? 'تسجيل الدخول باستخدام OTP' : (window.hyperOTPSettings?.loginHeader || translateInit('loginSignup'))}</h2>
    <div id="otp-message" style="margin-bottom: 1rem; color: green;"></div>
    <form id="otp-form" autocomplete="off">
      <input type="hidden" name="step" value="send" />
      <div class="input-group">
        <select id="country-code">
            ${generateCountryOptions().map(country =>
    `<option value="${country.code}" ${selectedCountryCode && country.code === selectedCountryCode ? 'selected' : ''}>${country.code}</option>`
  ).join('')}
        </select>
        <input name="identifier" placeholder="${translateInit('phoneNumber')}" value="${prefillPhone ? prefillPhone : ''}" />
      </div>
      <button type="submit" class="otp-btn">${translateInit('getOTP')}</button>
    </form>
    <div class="divider"><span style="font-size: 12px; font-weight: 600; color: #000000;">${translateInit('or')}</span></div>
    <div class="social-row">
      ${!loadingSocial ? `
        ${getEnabledSocialLogins().google ? `
          <button type="button" class="social-btn google-sso-btn">
            <img src='https://img.icons8.com/color/48/google-logo.png' alt='Google' />
          </button>
        ` : ''}
        ${getEnabledSocialLogins().facebook ? `
          <button type="button" class="otp-social-btn social-btn facebook-sso-btn">
            <img src='https://img.icons8.com/fluency/48/facebook-new.png' alt='Facebook' />
            <span style="font-size:0;">Facebook</span>
          </button>
        ` : ''}
      ` : ''}
    </div>
        <button type="button" class="email-btn" disabled style="width: 100% !important; background: #fff !important; border: 1px solid #ddd !important; border-radius: 8px !important; line-height: 45px !important; font-size: 18px !important; font-family: Inter, sans-serif !important; font-weight: 500 !important; cursor: pointer !important; color: #303030 !important; margin: 0 !important; padding: 0 !important; box-shadow: none !important; outline: none !important; text-decoration: none !important; text-transform: none !important; letter-spacing: normal !important;">${translateInit('loginEmailPassword')}</button>
        ${!shouldHidePoweredBy() ? `<div style="font-size: 10px; text-align: right; padding-top: 8px;">${translateInit('poweredBy')}</div>` : ''}
  `;

  // Attach OTP form handler immediately
  const otpForm = otpContainer.querySelector('#otp-form');
  if (otpForm) {
    attachOtpFormHandler(otpForm, otpContainer);
    // Remove whitespace as user types in phone input
    const phoneInput = otpForm.querySelector('input[name="identifier"]');
    if (phoneInput) {
      phoneInput.addEventListener('input', function (e) {
        const cursor = phoneInput.selectionStart;
        const newValue = phoneInput.value.replace(/\s+/g, '');
        if (phoneInput.value !== newValue) {
          phoneInput.value = newValue;
          phoneInput.setSelectionRange(cursor - 1, cursor - 1);
        }
      });
    }
  }
  // Fetch geo-location to auto-select country
  fetchGeoLocation(otpContainer);

  // Apply dynamic button colors
  applyDynamicButtonColors();

  // Apply placeholder protection
  applyPlaceholderProtection(otpContainer);

  // Start placeholder protection observer
  startPlaceholderProtectionObserver();

  // Force apply email button styles to prevent theme overrides
  const emailBtn = otpContainer.querySelector('.email-btn');
  if (emailBtn) {
    // Apply inline styles as a final fallback
    emailBtn.style.setProperty('width', '100%', 'important');
    emailBtn.style.setProperty('background', '#fff', 'important');
    emailBtn.style.setProperty('border', '1px solid #ddd', 'important');
    emailBtn.style.setProperty('border-radius', '8px', 'important');
    emailBtn.style.setProperty('line-height', '45px', 'important');
    emailBtn.style.setProperty('font-size', '18px', 'important');
    emailBtn.style.setProperty('font-family', 'Inter, sans-serif', 'important');
    emailBtn.style.setProperty('font-weight', '500', 'important');
    emailBtn.style.setProperty('cursor', 'pointer', 'important');
    emailBtn.style.setProperty('color', '#303030', 'important');
    emailBtn.style.setProperty('margin', '0', 'important');
    emailBtn.style.setProperty('padding', '0', 'important');
    emailBtn.style.setProperty('box-shadow', 'none', 'important');
    emailBtn.style.setProperty('outline', 'none', 'important');
    emailBtn.style.setProperty('text-decoration', 'none', 'important');
    emailBtn.style.setProperty('text-transform', 'none', 'important');
    emailBtn.style.setProperty('letter-spacing', 'normal', 'important');

    // Add event listeners to maintain styles on hover/focus
    emailBtn.addEventListener('mouseenter', function () {
      this.style.setProperty('background', '#f0f0f0', 'important');
      this.style.setProperty('border-color', '#bbb', 'important');
    });
    emailBtn.addEventListener('mouseleave', function () {
      this.style.setProperty('background', '#fff', 'important');
      this.style.setProperty('border-color', '#ddd', 'important');
    });
    emailBtn.addEventListener('focus', function () {
      this.style.setProperty('outline', 'none', 'important');
      this.style.setProperty('box-shadow', 'none', 'important');
    });

    // Enable the button (important for sidebar and all contexts)
    emailBtn.disabled = false;
    emailBtn.style.setProperty('cursor', 'pointer', 'important');
    emailBtn.style.setProperty('opacity', '1', 'important');

    // Add click handler to show Shopify default login form
    emailBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation(); // Prevent sidebar from closing
      e.stopImmediatePropagation(); // Stop all other handlers

      console.log('📧 Email login button clicked - showing Shopify default login');

      // INLINE LOGIC - Show Shopify default login form
      console.log('🔄 Showing Shopify default login form...');

      // Detect if we're in a sidebar context
      // distinct var name to avoid shadowing the 'otpContainer' argument from renderInitialForm
      const otpContainerEl = document.getElementById('otp-login-container') || document.getElementById('otp-sidebar-container');

      const isInSidebar = otpContainerEl && (
        otpContainerEl.id === 'otp-sidebar-container' ||
        otpContainerEl.closest('.halo-sidebar-wrapper') ||
        otpContainerEl.closest('[data-sidebar]') ||
        otpContainerEl.closest('.sidebar')
      );

      // Also check if the 'otpContainer' argument is in the sidebar (fallback)
      const containerArgInSidebar = otpContainer && (
        otpContainer.id === 'otp-sidebar-container' ||
        otpContainer.closest('.halo-sidebar-wrapper') ||
        otpContainer.closest('[data-sidebar]') ||
        otpContainer.closest('.sidebar')
      );

      // CRITICAL FIX: If we are on the actual /account/login page, we MUST treat this as the Main Page context,
      // even if the container happens to be inside a wrapper that looks like a sidebar.
      // This ensures we correctly remove the global hiding style and show the main login form.
      const isOnLoginPage = isLoginPage();
      const effectiveSidebarCtx = !isOnLoginPage && (isInSidebar || containerArgInSidebar);

      console.log('📍 Context detected:', effectiveSidebarCtx ? 'Sidebar' : 'Main Page');
      console.log('📦 OTP Container (DOM):', otpContainerEl ? otpContainerEl.id : 'NOT FOUND');

      if (effectiveSidebarCtx) {
        // SIDEBAR CONTEXT
        console.log('🔧 Handling sidebar context...');

        // FIRST: Find and show the hidden sidebar login form BEFORE removing OTP
        const sidebar = document.querySelector('[data-auth-sidebar]');
        const sidebarLoginForm = sidebar ? sidebar.querySelector('.auth-form.auth-form-2') : null;

        if (sidebarLoginForm) {
          // Show the form first
          sidebarLoginForm.style.display = 'block';
          sidebarLoginForm.style.visibility = 'visible';
          sidebarLoginForm.style.opacity = '1';
          console.log('✅ Sidebar login form is now visible');
        } else {
          console.warn('⚠️ Sidebar login form not found');
          // Try generic fallback
          const anyForm = sidebar ? sidebar.querySelector('form') : null;
          if (anyForm) {
            anyForm.style.display = 'block';
            anyForm.style.visibility = 'visible';
            anyForm.style.opacity = '1';
          }
        }

        // THEN: Hide OTP container
        // Prefer the argument container if available
        if (otpContainer) {
          setTimeout(() => {
            otpContainer.style.display = 'none';
            console.log('✅ OTP container (arg) hidden from sidebar');
          }, 50);
        } else if (otpContainerEl) {
          setTimeout(() => {
            otpContainerEl.style.display = 'none';
            console.log('✅ OTP container (DOM) hidden from sidebar');
          }, 50);
        }

      } else {
        // MAIN PAGE CONTEXT
        console.log('🔧 Handling main page context...');

        // Remove OTP Container - robustly
        if (otpContainer) {
          otpContainer.remove();
          console.log('✅ OTP container (arg) removed');
        }
        if (otpContainerEl && document.contains(otpContainerEl)) {
          otpContainerEl.remove();
          console.log('✅ OTP container (DOM) removed');
        }

        // Remove hiding CSS
        const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
        if (tempStyle) tempStyle.remove();

        const hideStyle = document.getElementById('hyperotp-hide-login-style');
        if (hideStyle) hideStyle.remove();

        // Show all hidden Shopify login elements - include the specific class we added
        const hiddenElements = document.querySelectorAll('.login-row, .login-rowItem, form[action="/account/login"], #CustomerLoginForm, .account-page.order-page');
        hiddenElements.forEach(el => {
          el.style.display = '';
          el.style.visibility = '';
          el.style.height = '';
          el.style.opacity = '';
          el.style.pointerEvents = '';
          el.style.position = '';
          el.style.left = '';
          el.style.zIndex = '';
        });
      }

      console.log('✅ Done!');
    });
  }
}

// Function to apply dynamic button colors based on settings
function applyDynamicButtonColors() {
  const settings = window.hyperOTPSettings;
  if (!settings) return;

  const buttonColor = settings.buttonColor || '#ffff';
  const textColor = settings.textColor || '#ffffff';

  // Create or update dynamic button styles
  let dynamicButtonStyle = document.getElementById('hyperotp-dynamic-button-styles');
  if (!dynamicButtonStyle) {
    dynamicButtonStyle = document.createElement('style');
    dynamicButtonStyle.id = 'hyperotp-dynamic-button-styles';
    document.head.appendChild(dynamicButtonStyle);
  }

  // Apply dynamic colors to all buttons
  dynamicButtonStyle.innerHTML = `
    .otp-btn {
      background: ${buttonColor} !important;
      color: ${textColor} !important;
      box-shadow: none !important;
    }
    .otp-btn:hover {
      background: ${adjustColorBrightness(buttonColor, -20)} !important;
      box-shadow: none !important;
    }
    .otp-verify-btn {
      background: ${buttonColor} !important;
      color: ${textColor} !important;
      box-shadow: none !important;
    }
    .otp-verify-btn:hover {
      background: ${adjustColorBrightness(buttonColor, -20)} !important;
      box-shadow: none !important;
    }
    .signup-btn {
      background: ${buttonColor} !important;
      color: ${textColor} !important;
      box-shadow: none !important;
    }
    .signup-btn:hover {
      background: ${adjustColorBrightness(buttonColor, -20)} !important;
      box-shadow: none !important;
    }
  `;
}

// Helper function to adjust color brightness
function adjustColorBrightness(hex, percent) {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse the hex color
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Adjust brightness
  const factor = 1 + (percent / 100);
  const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
  const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
  const newB = Math.min(255, Math.max(0, Math.round(b * factor)));

  // Convert back to hex
  return '#' +
    (newR < 16 ? '0' : '') + newR.toString(16) +
    (newG < 16 ? '0' : '') + newG.toString(16) +
    (newB < 16 ? '0' : '') + newB.toString(16);
}

/**
 * Applies placeholder styles directly to input elements to protect against theme overrides
 * This function uses inline styles as a fallback protection mechanism
 */
function applyPlaceholderProtection(container) {
  if (!container) container = document.getElementById('otp-login-container');
  if (!container) return;

  // Phone number input placeholder
  const phoneInputs = container.querySelectorAll('input[name="identifier"]');
  phoneInputs.forEach(input => {
    if (input.placeholder) {
      // Store original placeholder
      if (!input.dataset.originalPlaceholder) {
        input.dataset.originalPlaceholder = input.placeholder;
      }
      // Apply via style attribute as additional protection
      input.style.setProperty('--placeholder-color', '#8A8A8A', 'important');
    }
  });

  // OTP input placeholder - always centered
  const otpInputs = container.querySelectorAll('input[name="otp"], .otp-verify-input[name="otp"]');
  otpInputs.forEach(input => {
    if (input.placeholder) {
      if (!input.dataset.originalPlaceholder) {
        input.dataset.originalPlaceholder = input.placeholder;
      }
      // Force center alignment via inline style
      const style = window.getComputedStyle(input, '::placeholder');
      if (style.textAlign !== 'center') {
        // Use a style element with higher specificity
        const styleId = 'hyperotp-otp-placeholder-' + Date.now();
        let styleEl = document.getElementById(styleId);
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = styleId;
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = `
          #otp-login-container input[name="otp"]::placeholder,
          #otp-login-container .otp-verify-input[name="otp"]::placeholder {
            text-align: center !important;
            text-align-last: center !important;
            color: #8A8A8A !important;
            opacity: 1 !important;
            font-size: 20px !important;
            letter-spacing: normal !important;
          }
        `;
      }
    }
  });

  // Email input placeholder
  const emailInputs = container.querySelectorAll('input[name="email"], .email-input, .signup-input[type="email"]');
  emailInputs.forEach(input => {
    if (input.placeholder) {
      if (!input.dataset.originalPlaceholder) {
        input.dataset.originalPlaceholder = input.placeholder;
      }
    }
  });

  // Signup form input placeholders
  const signupInputs = container.querySelectorAll('.signup-input, input[name="first_name"], input[name="last_name"]');
  signupInputs.forEach(input => {
    if (input.placeholder) {
      if (!input.dataset.originalPlaceholder) {
        input.dataset.originalPlaceholder = input.placeholder;
      }
    }
  });
}

/**
 * Show Shopify default login form
 * Removes OTP container and all hiding CSS to reveal the default Shopify login
 * Handles both main login page and sidebar contexts
 */
function showShopifyDefaultLoginForm() {
  console.log('🚨 FUNCTION STARTED - LINE 1');
  console.log('🔄 Showing Shopify default login form...');

  // Detect if we're in a sidebar context
  // Check for both main page container (#otp-login-container) and sidebar container (#otp-sidebar-container)
  const otpContainer = document.getElementById('otp-login-container') || document.getElementById('otp-sidebar-container');
  const isInSidebar = otpContainer && (
    otpContainer.id === 'otp-sidebar-container' ||
    otpContainer.closest('.halo-sidebar-wrapper') ||
    otpContainer.closest('[data-sidebar]') ||
    otpContainer.closest('.sidebar') ||
    otpContainer.parentElement?.classList.contains('account-dropdown') ||
    otpContainer.parentElement?.id === 'account-dropdown'
  );

  console.log('📍 Context detected:', isInSidebar ? 'Sidebar' : 'Main Page');
  console.log('📦 OTP Container:', otpContainer ? otpContainer.id : 'NOT FOUND');

  if (isInSidebar) {
    // SIDEBAR CONTEXT: Just remove OTP container and show hidden sidebar login form
    console.log('🔧 Handling sidebar context...');

    if (otpContainer) {
      otpContainer.remove();
      console.log('✅ OTP container removed from sidebar');
    }

    // Find and show the hidden sidebar login form (this form was hidden by renderOTPInSidebar line 3637)
    // The form selector matches detectLoginContext() line 3517
    const sidebar = document.querySelector('[data-auth-sidebar]');
    const sidebarLoginForm = sidebar ? sidebar.querySelector('.auth-form.auth-form-2') : null;

    if (sidebarLoginForm) {
      sidebarLoginForm.style.display = 'block';
      sidebarLoginForm.style.visibility = '';
      sidebarLoginForm.style.opacity = '';
      sidebarLoginForm.style.height = '';
      sidebarLoginForm.style.position = '';
      sidebarLoginForm.style.left = '';
      console.log('✅ Sidebar login form is now visible');
    } else {
      console.warn('⚠️ Sidebar login form not found');
      console.log('🔍 Sidebar element:', sidebar);
    }

    // Show parent containers
    const parentContainers = document.querySelectorAll('.halo-sidebar-wrapper, .account-dropdown, .login-item, .login-row');
    parentContainers.forEach(el => {
      el.style.display = '';
      el.style.visibility = '';
      el.style.opacity = '';
      el.style.height = '';
    });


  } else {
    // MAIN PAGE CONTEXT: Remove OTP and show main login page form
    console.log('🔧 Handling main page context...');

    // Remove OTP container
    if (otpContainer) {
      otpContainer.remove();
      console.log('✅ OTP container removed');
    }

    // Remove all hiding CSS styles
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) {
      tempStyle.remove();
      console.log('✅ Temporary hide CSS removed');
    }

    const hideStyle = document.getElementById('hyperotp-hide-login-style');
    if (hideStyle) {
      hideStyle.remove();
      console.log('✅ Permanent hide CSS removed');
    }

    // Show all hidden Shopify login elements
    const hiddenElements = document.querySelectorAll(`
      .login-row,
      .login-rowItem,
      .login-rowLeft,
      .login-rowRight,
      .login-item,
      form[action="/account/login"],
      form[action*="customer_login"],
      .customer-login-form,
      #CustomerLoginForm,
      .account-form,
      .login-form
    `);

    hiddenElements.forEach(el => {
      el.style.display = '';
      el.style.visibility = '';
      el.style.height = '';
      el.style.opacity = '';
      el.style.pointerEvents = '';
      el.style.position = '';
      el.style.left = '';
      el.style.zIndex = '';
    });
  }

  console.log('✅ Shopify default login form is now visible');
}

// Function to inject CSS to hide Shopify login form, only if appEnabled is true
function injectHideLoginCSS() {
  if (document.getElementById('hyperotp-hide-login-style')) return;
  const style = document.createElement("style");
  style.id = "hyperotp-hide-login-style";
  style.innerHTML = `
    /* Hide all login/register forms and related elements */
    .login-row,
    .login-rowItem,
    .login-rowLeft,
    .login-rowRight,
    .login-item,
    .halo-page-wrapper,
    form[action="/account/login"],
    form[action*="customer_login"],
    form[action*="create_customer"],
    form[action*="recover_customer_password"],
    .customer-login-form,
    .create-customer-form,
    #customer_login,
    .customer.login,
    [data-login-form],
    .shopify-section--main-password,
    .shopify-section--main-login,
    .section-header__title,
    .login-title,
    .form-title,
    .customer-login-title,
    #CustomerLoginForm,
    #RecoverPassword,
    #CreateCustomer,
    .account-form,
    .login-form,
    h1.title.row,
    .page-header,
    /* Hide specific theme elements */
    .account-page .halo-page-content .login-row,
    .account-page .halo-page-content .login-rowItem,
    .account-page .halo-page-content h2,
    .account-page .halo-page-content .login-text,
    .account-page .halo-page-content form,
    .account-page .halo-page-content .form-field,
    .account-page .halo-page-content .form-actions,
    .account-page .halo-page-content a[href="#recover"],
    .account-page .halo-page-content a[href="#login"],
    /* Hide breadcrumb on login page */
    .account-page .breadcrumb,
    /* Hide any h1/h2 that's not in OTP container */
    .account-page h1:not(#otp-login-container h1):not(.login-page-wrapper h1),
    .account-page h2:not(#otp-login-container h2):not(.login-page-wrapper h2) {
      display: none !important;
      visibility: hidden !important;
      height: 0 !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      left: -9999px !important;
      z-index: -1 !important;
    }
    
    /* Ensure OTP container is visible but don't break style tags */
    #otp-login-container,
    #otp-injection-point,
    .login-page-wrapper {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      position: relative !important;
      left: auto !important;
      z-index: auto !important;
      pointer-events: auto !important;
    }
    
    /* Keep style tags hidden (they should not display) */
    #otp-login-container style,
    #otp-injection-point style,
    .login-page-wrapper style {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
  console.log('🎨 Injected CSS to hide default login forms');
}

// Function to show Shopify default login form and remove custom UI
function showShopifyDefaultLoginForm() {
  // Remove custom OTP UI with fade-out
  const otpContainer = document.getElementById('otp-login-container');
  if (otpContainer) {
    otpContainer.classList.add('otp-fade-out');
    setTimeout(() => {
      otpContainer.remove();
      // Remove both temp and permanent hide-login CSS
      const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
      if (tempStyle) tempStyle.remove();
      const permStyle = document.getElementById('hyperotp-hide-login-style');
      if (permStyle) permStyle.remove();
      // Optionally scroll to login form
      const loginForm = document.querySelector('form[action="/account/login"]') ||
        document.querySelector('.login-form') ||
        document.querySelector('.container .row .col-lg-6');
      if (loginForm) loginForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120); // match fade-out duration
  } else {
    // Remove both temp and permanent hide-login CSS
    const tempStyle = document.getElementById('hyperotp-temp-hide-login-style');
    if (tempStyle) tempStyle.remove();
    const permStyle = document.getElementById('hyperotp-hide-login-style');
    if (permStyle) permStyle.remove();
    // Optionally scroll to login form
    const loginForm = document.querySelector('form[action="/account/login"]') ||
      document.querySelector('.login-form') ||
      document.querySelector('.container .row .col-lg-6');
    if (loginForm) loginForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

// Attach handler to all email/password login buttons
function attachEmailPasswordHandlers() {
  document.querySelectorAll('.otp-email-btn, .email-btn').forEach(btn => {
    btn.removeEventListener('click', btn._emailLoginHandler);
    btn._emailLoginHandler = function (event) {
      event.preventDefault();
      showShopifyDefaultLoginForm();
      return false;
    };
    btn.addEventListener('click', btn._emailLoginHandler);
  });
}

// Initial attach for email/password login
attachEmailPasswordHandlers();

// Watch for dynamic UI changes
const emailBtnObserver = new MutationObserver(() => {
  attachEmailPasswordHandlers();
});
emailBtnObserver.observe(document.body, { childList: true, subtree: true });

// Add fade-out CSS for smooth transition
(function addOtpFadeOutCSS() {
  if (document.getElementById('otp-fade-out-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-fade-out-style';
  style.innerHTML = `
    #otp-login-container.otp-fade-out {
      opacity: 0;
      transition: opacity 0.12s ease;
    }
  `;
  document.head.appendChild(style);
})();

// Add fade-in CSS for OTP UI (in case of SSR or style removal)
(function addOtpFadeInCSS() {
  if (document.getElementById('otp-fade-in-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-fade-in-style';
  style.innerHTML = `
    .otp-fade-in {
      opacity: 0;
      transition: opacity 0.33s cubic-bezier(.4,0,.2,1);
    }
    .otp-fade-in[style*="opacity: 1"] {
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(style);
})();

// Add mobile margin/padding for OTP container
(function injectOtpMobileMarginCSS() {
  if (document.getElementById('otp-login-mobile-style')) return;
  const style = document.createElement('style');
  style.id = 'otp-login-mobile-style';
  style.innerHTML = `
    #otp-login-container {
      position: relative !important;
      // min-height: 400px !important;
      padding-bottom: 12px !important;
    }
    @media (max-width: 600px) {
      #otp-login-container {
        margin: 26px 36px !important;
        padding: 12px 8px 8px 8px !important;
        max-width: 98vw !important;
        position: relative !important;
        overflow: hidden !important;
      }
      .otp-verify-form-row {
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden !important;
      }
      .otp-verify-input {
        font-size: 20px !important;
        letter-spacing: 0.4em !important;
        height: 50px !important;
        margin-top: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
      .otp-verify-input::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
      }
      .otp-verify-input[name="otp"]::placeholder {
        text-align: center !important;
        text-align-last: center !important;
      }
      input[name="otp"]::placeholder {
        text-align: center !important;
        text-align-last: center !important;
      }
      .email-input {
        font-size: 16px !important;
        letter-spacing: normal !important;
        text-align: left !important;
        padding: 0 12px !important;
      }
      .email-input::placeholder {
        font-size: 14px !important;
        letter-spacing: normal !important;
      }
    }
  `;
  document.head.appendChild(style);
})();
// ============================================================================
// SIDEBAR OTP INTEGRATION
// This code should be appended to the end of login-override.js
// ============================================================================

/**
 * Detect if we're in sidebar or page context
 */
function detectLoginContext() {
  const sidebar = document.querySelector('[data-auth-sidebar]');
  const sidebarForm = sidebar ? sidebar.querySelector('.auth-form.auth-form-2') : null;
  const pageForm = document.querySelector('.customer-login-form');

  return {
    isSidebar: !!(sidebar && sidebarForm),
    isPage: !!pageForm,
    sidebar,
    sidebarForm,
    pageForm
  };
}

/**
 * Create and inject OTP container into sidebar
 */
function injectOTPContainerInSidebar() {
  const context = detectLoginContext();
  if (!context.isSidebar) return null;

  // Check if user is logged in (account dropdown exists instead of login form)
  const accountDropdown = context.sidebar.querySelector('.account-dropdown');
  if (accountDropdown) {
    console.log('ℹ️ User is logged in, skipping OTP injection');
    return null;
  }

  // Check if login form exists (user is logged out)
  if (!context.sidebarForm) {
    console.log('ℹ️ No login form found in sidebar, skipping OTP injection');
    return null;
  }

  // Check if already injected
  let otpContainer = context.sidebar.querySelector('#otp-sidebar-container');
  if (otpContainer) return otpContainer;

  // Create OTP container
  otpContainer = document.createElement('div');
  otpContainer.id = 'otp-sidebar-container';
  otpContainer.className = 'otp-login-wrapper';
  otpContainer.style.display = 'none';

  // Insert before the traditional form
  const wrapper = context.sidebar.querySelector('.halo-sidebar-wrapper');
  if (wrapper && context.sidebarForm) {
    wrapper.insertBefore(otpContainer, context.sidebarForm);
  }

  return otpContainer;
}

/**
 * Initialize OTP in sidebar
 */
function initializeSidebarOTP() {
  const context = detectLoginContext();
  if (!context.isSidebar || !context.sidebar) return;

  console.log('🎯 Initializing sidebar OTP...');

  // Inject container
  const otpContainer = injectOTPContainerInSidebar();
  if (!otpContainer) {
    console.warn('⚠️ Failed to inject OTP container in sidebar');
    return;
  }

  // Listen for body class changes (sidebar opens when body gets 'auth-sidebar-show' class)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const isOpen = document.body.classList.contains('auth-sidebar-show');

        if (isOpen && !otpContainer.hasAttribute('data-otp-initialized')) {
          console.log('📂 Sidebar opened, rendering OTP form...');
          renderOTPInSidebar(otpContainer, context);
        } else if (!isOpen && otpContainer.hasAttribute('data-otp-initialized')) {
          // Sidebar closed - reset for next open
          console.log('📂 Sidebar closed, resetting...');
          otpContainer.removeAttribute('data-otp-initialized');
        }
      }
    });
  });

  // Watch body element for class changes
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });

  // Also check if sidebar is already open
  const isOpen = document.body.classList.contains('auth-sidebar-show');
  if (isOpen && !otpContainer.hasAttribute('data-otp-initialized')) {
    console.log('📂 Sidebar already open, rendering OTP form...');
    renderOTPInSidebar(otpContainer, context);
  }
}

/**
 * Render OTP form in sidebar
 * Integrates with existing OTP rendering logic
 */
function renderOTPInSidebar(container, context) {
  if (container.hasAttribute('data-otp-initialized')) return;

  // Double-check user is not logged in
  const accountDropdown = context.sidebar.querySelector('.account-dropdown');
  if (accountDropdown) {
    console.log('ℹ️ User is logged in, skipping OTP rendering');
    return;
  }

  console.log('🎨 Rendering OTP in sidebar...');

  container.setAttribute('data-otp-initialized', 'true');
  container.style.display = 'block';

  // Hide traditional login form
  if (context.sidebarForm) {
    context.sidebarForm.style.display = 'none';
  }

  // Apply sidebar-specific styling
  container.style.cssText = `
        background: #fff;
        max-width: 100%;
        margin: 0;
        border-radius: 0;
        box-shadow: none;
        padding: 20px;
        font-family: 'Inter', sans-serif;
        display: block;
    `;

  // Wait for settings to load, then render the OTP form
  if (!window.hyperOTPSettings) {
    // Show loading state
    container.innerHTML = `
            <div style="padding: 20px; text-align: center;">
                <span class="otp-spinner" style="display: inline-block;"></span>
                <p style="margin-top: 10px; color: #666;">Loading...</p>
            </div>
        `;

    // Wait for settings
    hyperOTPSettingsPromise.then(settings => {
      window.hyperOTPSettings = settings;
      if (!settings || settings.appEnabled === false) {
        // Show traditional form
        if (context.sidebarForm) {
          context.sidebarForm.style.display = 'block';
        }
        container.style.display = 'none';
        return;
      }
      // Render the actual OTP form
      renderInitialForm(container, { loadingSocial: false });
      attachGoogleSSOHandlers();
      attachFacebookSSOHandlers();
      applyDynamicButtonColors();
    });
  } else {
    // Settings already loaded, render immediately
    if (!window.hyperOTPSettings || window.hyperOTPSettings.appEnabled === false) {
      // Show traditional form
      if (context.sidebarForm) {
        context.sidebarForm.style.display = 'block';
      }
      container.style.display = 'none';
      return;
    }
    // Render the actual OTP form
    renderInitialForm(container, { loadingSocial: false });
    attachGoogleSSOHandlers();
    attachFacebookSSOHandlers();
    applyDynamicButtonColors();
  }

  console.log('✅ OTP rendered in sidebar');
}

/**
 * Main initialization for sidebar OTP
 */
(function initializeOTPSystem() {
  console.log('🚀 Initializing OTP system...');

  function init() {
    // Wait a bit for custom elements to be defined
    setTimeout(() => {
      const context = detectLoginContext();

      // Initialize sidebar if present
      if (context.isSidebar) {
        console.log('🎯 Sidebar detected, initializing OTP...');
        initializeSidebarOTP();
      } else {
        console.log('ℹ️ No sidebar detected on this page');
      }

      // Page initialization is handled by the main DOMContentLoaded listener above
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
